import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { projectsAPI, componentsAPI, textBlocksAPI, offersAPI, seasonalDiscountsAPI, pricingEngineAPI, installationCostsAPI, getErrorMessage } from "../lib/api";
// Matrix mode has been deprecated - all pricing now uses CATALOG_COST
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Calculator, Save, Sun, Battery, Zap, Box, Wrench, Percent, Calendar, Shield, Upload, X, Image as ImageIcon, EyeOff, PieChart, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// ============== INDEPENDENCE FORECAST PREVIEW COMPONENT ==============
function IndependenceForecastPreview({ consumption, targetAutarky, pvSizeKwp, roofOrientation, manualYield, storageKwh }) {
  // Conservative yield factors
  const yieldFactors = {
    south: 900,
    east_west: 850,
    east: 820,
    west: 820,
    north: 650
  };

  const C = consumption;
  const A = Math.max(10, Math.min(95, targetAutarky)) / 100;
  
  // Autarky calculation
  const pvSupplyKwh = Math.round(C * A);
  const gridImportKwh = Math.round(C - pvSupplyKwh);
  const autarkyPercent = Math.round(A * 100);
  
  // Eigenverbrauch calculation
  let Y = manualYield;
  if (!Y && pvSizeKwp) {
    const yieldFactor = yieldFactors[roofOrientation] || 850;
    Y = pvSizeKwp * yieldFactor;
  }
  
  const eigenverbrauchAvailable = Y && Y > 0;
  let selfConsumptionRatio = null;
  
  if (eigenverbrauchAvailable) {
    const selfConsumptionKwh = Math.min(pvSupplyKwh, Y);
    selfConsumptionRatio = Math.round((selfConsumptionKwh / Y) * 100);
  }

  // Donut chart segments for display
  const autarkyDonut = {
    direktverbrauch: Math.round(autarkyPercent * 0.45),
    batterieentladung: autarkyPercent - Math.round(autarkyPercent * 0.45),
    netzbezug: 100 - autarkyPercent
  };

  const eigenDonut = eigenverbrauchAvailable ? {
    direktverbrauch: Math.round(selfConsumptionRatio * 0.40),
    batterieladung: Math.round(selfConsumptionRatio * 0.60),
    netzeinspeisung: 100 - selfConsumptionRatio
  } : null;

  // Mini donut chart component
  const MiniDonut = ({ segments, centerValue, label }) => {
    const total = segments.reduce((sum, s) => sum + s.value, 0);
    let currentAngle = -90;
    
    const paths = segments.map((seg, i) => {
      if (seg.value <= 0) return null;
      const angle = (seg.value / total) * 360;
      const endAngle = currentAngle + angle;
      const largeArc = angle > 180 ? 1 : 0;
      
      const x1 = 50 + 35 * Math.cos((currentAngle * Math.PI) / 180);
      const y1 = 50 + 35 * Math.sin((currentAngle * Math.PI) / 180);
      const x2 = 50 + 35 * Math.cos((endAngle * Math.PI) / 180);
      const y2 = 50 + 35 * Math.sin((endAngle * Math.PI) / 180);
      
      const pathData = `M 50 50 L ${x1} ${y1} A 35 35 0 ${largeArc} 1 ${x2} ${y2} Z`;
      currentAngle = endAngle;
      
      return <path key={i} d={pathData} fill={seg.color} />;
    });

    return (
      <div className="flex flex-col items-center">
        <svg viewBox="0 0 100 100" className="w-24 h-24">
          {paths}
          <circle cx="50" cy="50" r="20" fill="white" />
          <text x="50" y="53" textAnchor="middle" fontSize="14" fontWeight="bold" fill="#1f2937">
            {centerValue}%
          </text>
        </svg>
        <span className="text-xs text-muted-foreground mt-1">{label}</span>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-muted-foreground">PV-Ertrag (geschätzt):</span>
          <span className="font-medium ml-2">{Y ? `${Math.round(Y).toLocaleString('de-DE')} kWh` : 'n/a'}</span>
        </div>
        <div>
          <span className="text-muted-foreground">PV-Versorgung:</span>
          <span className="font-medium ml-2">{pvSupplyKwh.toLocaleString('de-DE')} kWh</span>
        </div>
        <div>
          <span className="text-muted-foreground">Netzbezug:</span>
          <span className="font-medium ml-2">{gridImportKwh.toLocaleString('de-DE')} kWh</span>
        </div>
        <div>
          <span className="text-muted-foreground">Speicher:</span>
          <span className="font-medium ml-2">{storageKwh?.toFixed(1)} kWh</span>
        </div>
      </div>

      <div className="flex justify-around pt-2">
        <MiniDonut 
          segments={eigenDonut ? [
            { value: eigenDonut.direktverbrauch, color: '#22c55e' },
            { value: eigenDonut.batterieladung, color: '#3b82f6' },
            { value: eigenDonut.netzeinspeisung, color: '#f59e0b' }
          ] : [{ value: 100, color: '#e5e7eb' }]}
          centerValue={eigenverbrauchAvailable ? selfConsumptionRatio : '—'}
          label="Eigenverbrauch"
        />
        <MiniDonut 
          segments={[
            { value: autarkyDonut.direktverbrauch, color: '#22c55e' },
            { value: autarkyDonut.batterieentladung, color: '#3b82f6' },
            { value: autarkyDonut.netzbezug, color: '#f59e0b' }
          ]}
          centerValue={autarkyPercent}
          label="Autarkiegrad"
        />
      </div>

      <div className="flex justify-center gap-6 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
          <span>Direktverbrauch</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
          <span>Batterie</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
          <span>Netz</span>
        </div>
      </div>
    </div>
  );
}

// ============== WIRTSCHAFTLICHKEITSVORSCHAU (BASELINE-LOGIK) ==============
// Prinzip: Ursprüngliche Kosten ohne PV vs. Neue Kosten mit PV + Einspeisung
function ProfitabilityPreview({ 
  pvYield, 
  investmentCost, 
  lifetimeYears = 25,
  currentGridPrice,
  dynamicTariff,
  feedInTariff,
  priceIncreasePct = 3,
  autarkyQuote,
  evSolarShare = 0.30,
  hasEV = false,
  evAnnualKm = 15000,
  evConsumption = 17,
  householdConsumption = 5000,
  surplusKwh = 0,
  // Legacy
  gridPrice, selfConsumptionQuote, evPvShare, batteryCapacity,
  // Wärmepumpe
  hasHeatPump, heatPumpConsumption, heatPumpPvShare,
  // Gas (Altkosten)
  gasConsumptionKwh, gasPriceEurPerKwh,
  // Mobilität (Altkosten)
  fuelCostPer100km,
  vehicleCostPer100kmOld, vehicleCostPer100kmEv
}) {
  // === BACKWARDS COMPATIBILITY ===
  if (!currentGridPrice && gridPrice) currentGridPrice = gridPrice;
  if (!dynamicTariff && gridPrice) dynamicTariff = gridPrice;
  if (!autarkyQuote && selfConsumptionQuote) autarkyQuote = selfConsumptionQuote;
  if (evPvShare !== undefined && evPvShare !== null) evSolarShare = evPvShare;

  if (!householdConsumption || householdConsumption <= 0 || !investmentCost || investmentCost <= 0 || !currentGridPrice || currentGridPrice <= 0) {
    return null;
  }

  const formatEur = (v) => v != null ? `${v.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €` : '-';
  const formatKwh = (v) => `${Math.round(v).toLocaleString('de-DE')} kWh`;

  // SCHRITT 1: STATUS QUO
  const electricityCostOld = householdConsumption * currentGridPrice;
  
  const hpDemand = hasHeatPump ? Math.round(parseFloat(heatPumpConsumption) || 0) : 0;
  const hpPvShareVal = parseFloat(heatPumpPvShare) || 0.30;
  const gasKwh = hasHeatPump ? (parseFloat(gasConsumptionKwh) || 0) : 0;
  const gasPrice = hasHeatPump ? (parseFloat(gasPriceEurPerKwh) || 0.12) : 0;
  const gasCostOld = gasKwh > 0 ? gasKwh * gasPrice : 0;
  
  const evDemandKwh = hasEV ? Math.round(evAnnualKm * evConsumption / 100) : 0;
  const evSolarKwh = Math.round(evDemandKwh * evSolarShare);
  const evGridKwh = evDemandKwh - evSolarKwh;
  const fuelPer100 = hasEV ? (parseFloat(fuelCostPer100km) || 0) : 0;
  const fuelCostOld = fuelPer100 > 0 ? (evAnnualKm / 100) * fuelPer100 : 0;
  
  const totalCostOld = electricityCostOld + gasCostOld + fuelCostOld;

  // SCHRITT 2+3: PV-NUTZUNG
  const pvUsedHousehold = Math.round(householdConsumption * (autarkyQuote || 0.30));
  const hpPvKwh = Math.round(hpDemand * hpPvShareVal);
  const hpGridKwh = hpDemand - hpPvKwh;
  const totalPvUsed = pvUsedHousehold + hpPvKwh + evSolarKwh;

  // SCHRITT 4: NETZBEZUG
  const householdGridKwh = householdConsumption - pvUsedHousehold;
  const gridConsumption = householdGridKwh + hpGridKwh + evGridKwh;
  const gridCost = gridConsumption * (dynamicTariff || currentGridPrice);

  // SCHRITT 5: EINSPEISUNG
  const calculatedSurplus = surplusKwh || (pvYield ? Math.max(0, pvYield - totalPvUsed) : 0);
  const feedInRevenue = calculatedSurplus * (feedInTariff || 0.078);

  // SCHRITT 6: NEUE KOSTEN
  const totalCostNew = gridCost - feedInRevenue;

  // SCHRITT 7: ERSPARNIS
  const annualSavings = totalCostOld - totalCostNew;
  const monthlySavings = annualSavings / 12;
  const amortization = annualSavings > 0 ? investmentCost / annualSavings : null;

  return (
    <div className="space-y-3 text-sm" data-testid="profitability-preview-v2">
      {/* BLOCK 1: STATUS QUO */}
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
        <h4 className="font-bold text-red-800 mb-2 text-base">Ihre Energiekosten heute</h4>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span>Stromkosten ({formatKwh(householdConsumption)} × {currentGridPrice.toFixed(2)} €/kWh)</span>
            <span className="font-medium">{formatEur(electricityCostOld)}</span>
          </div>
          {hasHeatPump && gasCostOld > 0 && (
            <div className="flex justify-between text-orange-700">
              <span>Gaskosten ({formatKwh(gasKwh)} × {gasPrice.toFixed(2)} €/kWh)</span>
              <span className="font-medium">{formatEur(gasCostOld)}</span>
            </div>
          )}
          {hasEV && fuelCostOld > 0 && (
            <div className="flex justify-between text-violet-700">
              <span>Kraftstoff ({evAnnualKm.toLocaleString('de-DE')} km × {fuelPer100.toFixed(2)} €/100km)</span>
              <span className="font-medium">{formatEur(fuelCostOld)}</span>
            </div>
          )}
          <div className="flex justify-between pt-1 border-t border-red-300 font-bold text-red-900">
            <span>Gesamte Energiekosten/Jahr</span>
            <span>{formatEur(totalCostOld)}</span>
          </div>
        </div>
      </div>

      {/* BLOCK 2: NEUE WELT */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <h4 className="font-bold text-blue-800 mb-2 text-base">Ihre Energiekosten mit System</h4>
        <div className="space-y-1">
          <div className="flex justify-between">
            <span>Reststrom ({formatKwh(gridConsumption)} × {(dynamicTariff || currentGridPrice).toFixed(2)} €/kWh)</span>
            <span className="font-medium">{formatEur(gridCost)}</span>
          </div>
          {feedInRevenue > 0 && (
            <div className="flex justify-between text-green-700">
              <span>Einspeisevergütung ({Math.round(calculatedSurplus).toLocaleString('de-DE')} kWh × {(feedInTariff || 0.078).toFixed(3)} €)</span>
              <span className="font-medium">- {formatEur(feedInRevenue)}</span>
            </div>
          )}
          <div className="flex justify-between pt-1 border-t border-blue-300 font-bold text-blue-900">
            <span>Gesamte Energiekosten/Jahr</span>
            <span>{formatEur(Math.max(0, totalCostNew))}</span>
          </div>
        </div>
      </div>

      {/* BLOCK 3: VORTEIL */}
      <div className="p-4 bg-green-100 border-2 border-green-400 rounded-lg">
        <h4 className="font-bold text-green-800 mb-2 text-base">Ihr Vorteil</h4>
        <div className="space-y-1">
          <div className="flex justify-between text-lg font-bold text-green-800">
            <span>Jährliche Einsparung</span>
            <span>+{formatEur(annualSavings)}</span>
          </div>
          <div className="flex justify-between text-green-700">
            <span>Monatliche Einsparung</span>
            <span>+{formatEur(monthlySavings)}</span>
          </div>
          {amortization && (
            <div className="flex justify-between text-green-700">
              <span>Amortisation (statisch)</span>
              <span>{amortization.toFixed(1)} Jahre</span>
            </div>
          )}
        </div>
      </div>

      {/* Vertriebstext */}
      <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 italic">
        Sie ersetzen aktuell jährliche Energiekosten in Höhe von <strong>{formatEur(totalCostOld)}</strong> durch 
        neue Restkosten von nur <strong>{formatEur(Math.max(0, totalCostNew))}</strong>. 
        Das entspricht einer jährlichen Entlastung von <strong>{formatEur(annualSavings)}</strong>.
      </div>

      {/* Dynamische Berechnung */}
      {priceIncreasePct > 0 && (
        <DynamicCalculationNew
          annualBenefit={annualSavings}
          investmentCost={investmentCost}
          priceIncreasePct={priceIncreasePct}
          lifetimeYears={lifetimeYears}
          totalConsumption={householdConsumption + hpDemand + evDemandKwh}
          totalGridKwh={gridConsumption}
          currentGridPrice={currentGridPrice}
          dynamicTariff={dynamicTariff || currentGridPrice}
          feedInRevenue={feedInRevenue}
          totalCostOld={totalCostOld}
          amortizationStatic={amortization}
        />
      )}

      {/* Details */}
      <details className="text-xs text-slate-500">
        <summary className="cursor-pointer hover:text-slate-700">Detailinformationen anzeigen</summary>
        <div className="mt-2 space-y-1 p-2 bg-slate-50 rounded">
          <div>Haushalt: {formatKwh(householdConsumption)} | PV-gedeckt: {formatKwh(pvUsedHousehold)} ({((autarkyQuote || 0.30) * 100).toFixed(0)}%)</div>
          {hasHeatPump && hpDemand > 0 && (
            <div>Wärmepumpe: {formatKwh(hpDemand)} | PV: {formatKwh(hpPvKwh)} ({(hpPvShareVal * 100).toFixed(0)}%) | Netz: {formatKwh(hpGridKwh)}</div>
          )}
          {hasEV && evDemandKwh > 0 && (
            <div>E-Auto: {formatKwh(evDemandKwh)} | PV: {formatKwh(evSolarKwh)} ({(evSolarShare * 100).toFixed(0)}%) | Netz: {formatKwh(evGridKwh)}</div>
          )}
          <div>Gesamt Netzbezug: {formatKwh(gridConsumption)} | Einspeisung: {formatKwh(Math.round(calculatedSurplus))}</div>
          <div>Autarkiegrad: {(totalPvUsed / (householdConsumption + hpDemand + evDemandKwh) * 100).toFixed(0)}%</div>
        </div>
      </details>

      <div className="text-xs text-slate-500 italic p-2 bg-slate-50 rounded border border-slate-200">
        <p className="font-medium">Hinweis: Alle Angaben zur Wirtschaftlichkeitsbetrachtung sind ohne Gewähr und vom individuellen Nutzerverhalten abhängig.</p>
      </div>
    </div>
  );
}

// ============== DYNAMISCHE BERECHNUNG MIT STROMPREISSTEIGERUNG (BASELINE-LOGIK) ==============
// Prinzip: Vorteil = Baseline-Kosten - Neue Reststromkosten + Einspeisung (mit Preissteigerung)
function DynamicCalculationNew({ 
  totalConsumption,
  totalGridKwh,
  currentGridPrice,
  dynamicTariff,
  feedInRevenue,
  totalCostOld,
  priceIncreasePct = 3, 
  lifetimeYears = 25, 
  investmentCost,
  amortizationStatic
}) {
  const priceIncreaseRate = priceIncreasePct / 100;
  
  // Fallbacks
  const safeTotalConsumption = totalConsumption || 0;
  const safeTotalGridKwh = totalGridKwh || 0;
  const safeCurrentGridPrice = currentGridPrice || 0.35;
  const safeDynamicTariff = dynamicTariff || safeCurrentGridPrice;
  const safeFeedInRevenue = feedInRevenue || 0;
  
  // Berechne jährliche Vorteile über Lebensdauer
  let cumulativeCashflow = 0;
  let dynamicAmortization = null;
  let yearlyBenefits = [];
  
  for (let year = 1; year <= lifetimeYears; year++) {
    // Strompreise im Jahr t (beide steigen!)
    const gridPriceYear = safeCurrentGridPrice * Math.pow(1 + priceIncreaseRate, year);
    const dynamicTariffYear = safeDynamicTariff * Math.pow(1 + priceIncreaseRate, year);
    
    // BASELINE im Jahr t = Gesamtverbrauch × gestiegener Strompreis
    const baselineYear = safeTotalConsumption * gridPriceYear;
    
    // NEUE RESTSTROMKOSTEN im Jahr t = Netzbezug × gestiegener dynamischer Tarif
    const newGridCostYear = safeTotalGridKwh * dynamicTariffYear;
    
    // JÄHRLICHER VORTEIL im Jahr t = Baseline - Reststrom + Einspeisung
    const yearlyBenefit = baselineYear - newGridCostYear + safeFeedInRevenue;
    
    // Kumulative Summe
    const prevCumulative = cumulativeCashflow;
    cumulativeCashflow += yearlyBenefit;
    
    // Dynamische Amortisation
    if (dynamicAmortization === null && cumulativeCashflow >= investmentCost) {
      const remaining = investmentCost - prevCumulative;
      const fraction = remaining / yearlyBenefit;
      dynamicAmortization = year - 1 + fraction;
    }
    
    // Speichere Beispieljahre
    if ([1, 5, 10, 15, 20, 25].includes(year) && year <= lifetimeYears) {
      yearlyBenefits.push({
        year,
        gridPrice: gridPriceYear,
        dynamicTariff: dynamicTariffYear,
        baseline: baselineYear,
        newCost: newGridCostYear,
        benefit: yearlyBenefit,
        cumulative: cumulativeCashflow
      });
    }
  }
  
  const averageYearlyBenefit = cumulativeCashflow / lifetimeYears;
  
  const formatEur = (val) => val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
  const formatEurKwh = (val) => val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €/kWh';
  
  return (
    <div className="space-y-2">
      {/* Dynamische Kennzahlen */}
      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
        <div className="font-medium text-emerald-700 text-xs mb-2">
          LANGZEITBETRACHTUNG ({priceIncreasePct}% Strompreissteigerung p.a.)
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-emerald-600">Ø Jahresvorteil:</span>
            <span className="font-bold text-emerald-700 ml-2">{formatEur(averageYearlyBenefit)}</span>
          </div>
          <div>
            <span className="text-emerald-600">Gesamtvorteil:</span>
            <span className="font-bold text-emerald-700 ml-2">{formatEur(cumulativeCashflow)}</span>
          </div>
        </div>
        {dynamicAmortization && (
          <div className="mt-2 pt-2 border-t border-emerald-200 text-sm">
            <span className="text-emerald-600">Amortisation (dynamisch):</span>
            <span className="font-bold text-emerald-700 ml-2">{dynamicAmortization.toFixed(1)} Jahre</span>
            <span className="text-xs text-emerald-500 ml-2">
              (vs. {amortizationStatic?.toFixed(1) || 'nicht berechenbar'} Jahre statisch)
            </span>
          </div>
        )}
      </div>
      
      {/* Entwicklung über die Jahre */}
      {yearlyBenefits.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer p-2 bg-slate-50 rounded border font-medium text-slate-600">
            Entwicklung über {lifetimeYears} Jahre anzeigen
          </summary>
          <div className="mt-1 p-2 bg-slate-50 rounded border">
            <div className="grid grid-cols-4 gap-1 text-slate-500 font-medium border-b pb-1">
              <div>Jahr</div>
              <div className="text-right">Strompreis</div>
              <div className="text-right">Vorteil</div>
              <div className="text-right">Kumuliert</div>
            </div>
            {yearlyBenefits.map(({ year, gridPrice, benefit, cumulative }) => (
              <div key={year} className={`grid grid-cols-4 gap-1 py-0.5 ${
                dynamicAmortization && Math.abs(year - dynamicAmortization) < 1.5 
                  ? 'bg-emerald-100 font-medium' 
                  : ''
              }`}>
                <div>Jahr {year}</div>
                <div className="text-right">{formatEurKwh(gridPrice)}</div>
                <div className="text-right">{formatEur(benefit)}</div>
                <div className="text-right">{formatEur(cumulative)}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ============== WIRTSCHAFTLICHKEITSVORSCHAU WRAPPER ==============
// WICHTIG: Diese Funktion leitet alle Parameter DIREKT an ProfitabilityPreview weiter!
// - baselinePrice = bisheriger Strompreis für Ersparnis-Berechnung (currentGridPrice)
// - dynamicPrice = dynamischer Tarif für Reststromkosten (dynamicTariff)
function SwitchCalculationPreview({ 
  consumption, 
  targetAutarky, 
  pvYield, 
  baselinePrice,   // WICHTIG: Bisheriger Strompreis für ERSPARNIS (z.B. 0.35 €/kWh)
  dynamicPrice,    // WICHTIG: Dynamischer Tarif für RESTSTROMKOSTEN (z.B. 0.22 €/kWh)
  feedInTariff, 
  evAnnualKm = 15000, 
  evConsumption = 17,
  // NEUE Parameter für korrekte Berechnung
  investmentCost,
  selfConsumptionQuote,
  evSolarShare,
  hasEV,
  hasHeatPump,
  heatPumpConsumption,
  heatPumpPvShare,
  gasConsumptionKwh,
  gasPriceEurPerKwh,
  fuelCostPer100km,
  vehicleCostPer100kmOld,
  vehicleCostPer100kmEv,
  batteryCapacity,
  priceIncreasePct
}) {
  // STRIKTE TRENNUNG DER PREISE:
  // - currentGridPrice (baselinePrice) = für Ersparnis (vermiedener Netzbezug)
  // - dynamicTariff (dynamicPrice) = für Reststromkosten
  const currentGridPrice = baselinePrice || 0.35;
  const dynamicTariff = dynamicPrice || currentGridPrice; // Fallback: wenn kein dynamicPrice, dann baselinePrice
  
  // Autarkiequote aus targetAutarky (Prozent -> Dezimal) oder selfConsumptionQuote
  const autarkyQuote = selfConsumptionQuote 
    ? parseFloat(selfConsumptionQuote) 
    : (targetAutarky ? targetAutarky / 100 : 0.30);
  
  // Überschuss berechnen
  const householdAutarky = consumption * autarkyQuote;
  const evDemand = hasEV ? evAnnualKm * evConsumption / 100 : 0;
  const evSolar = evDemand * (evSolarShare ? parseFloat(evSolarShare) : 0.30);
  const hpPvShareVal = parseFloat(heatPumpPvShare) || 0.30;
  const hpPv = hasHeatPump ? (parseFloat(heatPumpConsumption) || 0) * hpPvShareVal : 0;
  const totalSelfUse = householdAutarky + evSolar + hpPv;
  const surplus = pvYield ? Math.max(0, pvYield - totalSelfUse) : 0;
  
  return (
    <ProfitabilityPreview
      pvYield={pvYield}
      investmentCost={investmentCost || 20000}
      lifetimeYears={25}
      currentGridPrice={currentGridPrice}
      dynamicTariff={dynamicTariff}
      feedInTariff={feedInTariff}
      priceIncreasePct={priceIncreasePct || 3}
      autarkyQuote={autarkyQuote}
      evSolarShare={evSolarShare ? parseFloat(evSolarShare) : 0.30}
      hasEV={hasEV || false}
      evAnnualKm={evAnnualKm}
      evConsumption={evConsumption}
      householdConsumption={consumption}
      surplusKwh={surplus}
      hasHeatPump={hasHeatPump || false}
      heatPumpConsumption={parseFloat(heatPumpConsumption) || 0}
      heatPumpPvShare={heatPumpPvShare}
      gasConsumptionKwh={gasConsumptionKwh}
      gasPriceEurPerKwh={gasPriceEurPerKwh}
      fuelCostPer100km={fuelCostPer100km}
      vehicleCostPer100kmOld={vehicleCostPer100kmOld}
      vehicleCostPer100kmEv={vehicleCostPer100kmEv}
    />
  );
}

export default function OfferCreatePage() {
  const { projectId, id: offerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEditMode = location.pathname.includes("/bearbeiten");
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [existingOffer, setExistingOffer] = useState(null);
  
  // Admin-Switch: Hide pricing table from customer view
  const [hidePricingTable, setHidePricingTable] = useState(false);
  
  // Installation images
  const [installationImages, setInstallationImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  
  // Data
  const [projects, setProjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [storages, setStorages] = useState([]);
  const [inverters, setInverters] = useState([]);
  const [textBlocks, setTextBlocks] = useState([]);
  const [outroTextBlocks, setOutroTextBlocks] = useState([]);  // Abschlusstexte
  // Pricing Engine Mode: Always CATALOG_COST (matrix mode deprecated)
  const [pricingMode, setPricingMode] = useState("CATALOG_COST");
  // State for missing cost components (for error display)
  const [missingCostError, setMissingCostError] = useState(null);
  const [emsKi, setEmsKi] = useState(null);
  const [meterCabinet, setMeterCabinet] = useState(null);
  const [grounding, setGrounding] = useState(null);
  const [scaffolding, setScaffolding] = useState(null);
  const [wallbox, setWallbox] = useState(null);
  const [activeSeasonalDiscount, setActiveSeasonalDiscount] = useState(null);
  const [installationCosts, setInstallationCosts] = useState({ dc_costs: [], ac_costs: [] });

  // Form
  const [formData, setFormData] = useState({
    project_id: projectId || "",
    tier: "",  // "standard" oder "secure_plus" - muss ausgewählt werden
    intro_text_id: "",
    outro_text_id: "",  // Abschlusstext
    module_id: "",
    module_count: 10,
    storage_id: "",
    storage_extension_count: 0,  // Anzahl der Speichererweiterungen (0-max_extensions)
    inverter_id: "",
    backup: false,  // Backup/Notstrom Option - 400€ Mehrpreis
    ems_ki: true,  // EMS-KI ist standardmäßig aktiviert (im Preis enthalten)
    meter_cabinet: false,
    grounding: false,
    scaffolding: "nein",
    wallbox: "nein",
    intermediate_meter_count: 0,  // Zwischenzähler - 100€ pro Stück
    insurance: false,
    discount_percent: 0,
    seasonal_discount_percent: 0,  // Saisonaler Rabatt - beeinflusst NICHT die Provision
    oversell_percent: 0,
    payment_terms: "standard",  // standard or prepayment
    notes: "",  // Notizen für das Angebot (erscheinen im PDF)
    // Unabhängigkeitsrechnung (konservativ)
    include_independence_forecast: false,
    annual_household_consumption_kwh: "",
    target_autarky_percent: 70,
    roof_orientation: "",
    annual_pv_yield_kwh: "",
    // Wechselrechnung: Stromkosten-Vorteil (konservativ)
    include_switch_calculation: true,
    baseline_grid_price_gross_eur_kwh: "0.35",
    avg_dynamic_rest_grid_price_gross_eur_kwh: "0.25",
    feed_in_tariff_eur_kwh: "0.082",
    // E-Auto / Elektromobilität
    ev_annual_km: "15000",
    ev_consumption_kwh_per_100km: "17",
    // === NEUE WIRTSCHAFTLICHKEITSBERECHNUNG ===
    investment_cost: "",  // Auto aus Angebotspreis
    lifetime_years: "25",
    price_increase_pct: "3",
    self_consumption_quote: "",  // Auto-geschätzt (leer = auto)
    ev_pv_share: "",  // Auto-geschätzt (leer = auto)
    has_ev: false,
    has_heat_pump: false,
    heat_pump_consumption_kwh: "",
    vehicle_cost_per_100km_old: "",
    vehicle_cost_per_100km_ev: "",
    gas_consumption_kwh: "",
    gas_price_eur_per_kwh: "0.12",
    heat_pump_pv_share: "0.30",
    fuel_cost_per_100km: ""
  });

  // Filter components based on selected tier
  const filterByTier = (items) => {
    if (!formData.tier) return [];  // Keine Komponenten zeigen bis Tier gewählt
    return items.filter(item => 
      item.availability === "both" || item.availability === formData.tier
    );
  };

  const filteredModules = filterByTier(modules);
  const filteredStorages = filterByTier(storages);
  const filteredInverters = filterByTier(inverters);
  const filteredTextBlocks = textBlocks.filter(tb => 
    (tb.tier === "both" || tb.tier === formData.tier || !formData.tier) && tb.active !== false
  );
  const filteredOutroTextBlocks = outroTextBlocks.filter(tb => 
    (tb.tier === "both" || tb.tier === formData.tier || !formData.tier) && tb.active !== false
  );

  // Selected storage with extension info
  const selectedStorage = storages.find(s => s.id === formData.storage_id);
  const hasExtension = selectedStorage?.max_extensions > 0;
  const maxExtensions = selectedStorage?.max_extensions || 0;

  // Pricing engine now uses CATALOG_COST mode exclusively

  // Calculated prices
  const [calculatedPrice, setCalculatedPrice] = useState({
    base: 0,
    totalCost: 0,  // NEU: Gesamtkosten (EK)
    calculatedBasePrice: 0,  // NEU: Kalkulierter Mindestpreis
    emsKiPrice: 0,  // EMS-KI Preis (wenn aktiviert)
    backup: 0,  // Backup Mehrpreis (400€)
    storageExtension: 0,  // Speichererweiterung Preis
    meterCabinet: 0,
    grounding: 0,
    scaffolding: 0,
    wallbox: 0,
    intermediateMeter: 0,  // Zwischenzähler Preis
    insurance: 0,
    subtotal: 0,
    discount: 0,
    seasonalDiscount: 0,  // Saisonaler Rabatt
    oversell: 0,
    total: 0,
    display: 0,
    skonto: 0,
    final: 0,
    actualMargin: 0,  // NEU: Tatsächliche Marge %
    commissionBase: 0  // NEU: Basis für Provisionsberechnung
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    calculatePrice();
  }, [formData, modules, storages, inverters, emsKi, meterCabinet, grounding, scaffolding, wallbox, selectedStorage, installationCosts]);

  // Reset component selections when tier changes
  useEffect(() => {
    if (formData.tier) {
      // Finde die passende Einleitung basierend auf dem Angebotstyp
      // Priorität: 1. Exakter Name-Match (Standard oder Secure_Plus)
      //            2. Passender Tier-Match
      //            3. "both" als Fallback
      const tierName = formData.tier === "standard" ? "Standard" : "Secure_Plus";
      
      // Suche nach Einleitung mit exaktem Namen (Standard oder Secure_Plus)
      let matchingIntro = textBlocks.find(tb => 
        tb.name === tierName && tb.active !== false
      );
      // Fallback: Suche nach Tier-Match
      if (!matchingIntro) {
        matchingIntro = textBlocks.find(tb => 
          (tb.tier === formData.tier || tb.tier === "both") && tb.active !== false
        );
      }
      
      // Für Outro: Suche nach passendem Tier
      const matchingOutro = outroTextBlocks.find(tb => 
        (tb.tier === formData.tier || tb.tier === "both") && tb.active !== false
      );
      
      setFormData(prev => ({
        ...prev,
        module_id: "",
        storage_id: "",
        inverter_id: "",
        storage_extension_count: 0,
        intro_text_id: matchingIntro?.id || "",
        outro_text_id: matchingOutro?.id || "",
        // Bei Secure Plus ist EMS-KI standardmäßig aktiviert, bei Standard nicht verfügbar
        ems_ki: formData.tier === "secure_plus" ? true : false
      }));
    }
  }, [formData.tier, textBlocks, outroTextBlocks]);

  // Auto-select appropriate inverter based on module count
  useEffect(() => {
    if (!formData.tier || filteredInverters.length === 0) return;
    
    const currentInverter = filteredInverters.find(i => i.id === formData.inverter_id);
    
    // Check if current inverter can handle the module count
    if (currentInverter?.max_modules && formData.module_count > currentInverter.max_modules) {
      // Find the smallest inverter that can handle the current module count
      const compatibleInverters = filteredInverters
        .filter(i => !i.max_modules || i.max_modules >= formData.module_count)
        .sort((a, b) => (a.max_modules || 999) - (b.max_modules || 999));
      
      if (compatibleInverters.length > 0) {
        // Also check backup capability - if backup is enabled, only select backup-capable inverters
        let newInverter = compatibleInverters[0];
        if (formData.backup) {
          const backupCompatible = compatibleInverters.find(i => i.backup_capable);
          if (backupCompatible) {
            newInverter = backupCompatible;
          }
        }
        
        setFormData(prev => ({ ...prev, inverter_id: newInverter.id }));
        toast.info(`Wechselrichter automatisch auf "${newInverter.name}" geändert (${formData.module_count} Module)`);
      }
    }
  }, [formData.module_count, formData.tier, filteredInverters]);

  const loadData = async () => {
    try {
      // Get pricing engine status (always CATALOG_COST now)
      try {
        const pricingStatus = await pricingEngineAPI.getStatus();
        setPricingMode(pricingStatus.data.mode);
        console.log("Pricing Engine Mode:", pricingStatus.data.mode);
      } catch (e) {
        console.warn("Could not get pricing mode, using CATALOG_COST:", e);
        setPricingMode("CATALOG_COST");
      }

      const [
        projectsRes,
        modulesRes,
        storagesRes,
        invertersRes,
        textBlocksRes,
        outroTextBlocksRes,
        emsKiRes,
        meterCabinetRes,
        groundingRes,
        scaffoldingRes,
        wallboxRes,
        seasonalDiscountRes,
        installationCostsRes
      ] = await Promise.all([
        projectsAPI.list(),
        componentsAPI.list("module"),
        componentsAPI.list("storage"),
        componentsAPI.list("inverter"),
        textBlocksAPI.list("intro"),
        textBlocksAPI.list("outro"),
        componentsAPI.list("ems_ki"),
        componentsAPI.list("meter_cabinet"),
        componentsAPI.list("grounding"),
        componentsAPI.list("scaffolding"),
        componentsAPI.list("wallbox"),
        seasonalDiscountsAPI.getActive(),
        installationCostsAPI.get().catch(() => ({ data: { dc_costs: [], ac_costs: [] } }))
      ]);

      setProjects(projectsRes.data);
      setModules(modulesRes.data);
      setStorages(storagesRes.data);
      setInverters(invertersRes.data);
      setTextBlocks(textBlocksRes.data);
      setOutroTextBlocks(outroTextBlocksRes.data);
      
      if (emsKiRes.data.length > 0) setEmsKi(emsKiRes.data[0]);
      if (meterCabinetRes.data.length > 0) setMeterCabinet(meterCabinetRes.data[0]);
      if (groundingRes.data.length > 0) setGrounding(groundingRes.data[0]);
      if (scaffoldingRes.data.length > 0) setScaffolding(scaffoldingRes.data[0]);
      if (wallboxRes.data.length > 0) setWallbox(wallboxRes.data[0]);
      
      // Set active seasonal discount
      if (seasonalDiscountRes.data) {
        setActiveSeasonalDiscount(seasonalDiscountRes.data);
      }
      
      // Set installation costs (DC/AC)
      setInstallationCosts(installationCostsRes.data || { dc_costs: [], ac_costs: [] });
      console.log("Installation costs loaded:", installationCostsRes.data);

      // If in edit mode, load existing offer data
      if (isEditMode && offerId) {
        try {
          const offerRes = await offersAPI.get(offerId);
          const offer = offerRes.data;
          setExistingOffer(offer);
          
          // Check if offer can be edited
          if (offer.pdf_generated) {
            toast.error("Angebot kann nicht bearbeitet werden, da bereits ein PDF erstellt wurde");
            navigate(`/angebote/${offerId}`);
            return;
          }
          
          if (offer.signature) {
            toast.error("Angebot kann nicht bearbeitet werden, da es bereits unterschrieben wurde");
            navigate(`/angebote/${offerId}`);
            return;
          }
          
          // Populate form with existing data
          setFormData({
            project_id: offer.project_id,
            tier: offer.tier || "",
            intro_text_id: "", // We don't store the text block ID, only the content
            module_id: offer.module_id,
            module_count: offer.module_count,
            storage_id: offer.storage_id,
            storage_extension_count: offer.storage_extension_count || 0,
            inverter_id: offer.inverter_id,
            ems_ki: offer.ems_ki || false,
            backup: offer.backup || false,
            meter_cabinet: offer.meter_cabinet,
            grounding: offer.grounding,
            scaffolding: offer.scaffolding,
            wallbox: offer.wallbox,
            intermediate_meter_count: offer.intermediate_meter_count || 0,
            insurance: offer.insurance || false,
            discount_percent: offer.discount_percent,
            seasonal_discount_percent: offer.seasonal_discount_percent || 0,
            oversell_percent: offer.oversell_percent,
            payment_terms: offer.payment_terms || "standard",
            // Unabhängigkeitsrechnung (konservativ)
            include_independence_forecast: offer.include_independence_forecast || false,
            annual_household_consumption_kwh: offer.annual_household_consumption_kwh || "",
            target_autarky_percent: offer.target_autarky_percent || 70,
            roof_orientation: offer.roof_orientation || "",
            annual_pv_yield_kwh: offer.annual_pv_yield_kwh || "",
            // Wechselrechnung
            include_switch_calculation: offer.include_switch_calculation !== false,
            baseline_grid_price_gross_eur_kwh: offer.baseline_grid_price_gross_eur_kwh || "",
            avg_dynamic_rest_grid_price_gross_eur_kwh: offer.avg_dynamic_rest_grid_price_gross_eur_kwh || "",
            feed_in_tariff_eur_kwh: offer.feed_in_tariff_eur_kwh || "",
            // E-Auto / Elektromobilität
            ev_annual_km: offer.ev_annual_km || "15000",
            ev_consumption_kwh_per_100km: offer.ev_consumption_kwh_per_100km || "17",
            // Wärmepumpe
            has_heat_pump: offer.has_heat_pump || false,
            heat_pump_consumption_kwh: offer.heat_pump_consumption_kwh || "",
            vehicle_cost_per_100km_old: offer.vehicle_cost_per_100km_old || "",
            vehicle_cost_per_100km_ev: offer.vehicle_cost_per_100km_ev || "",
            gas_consumption_kwh: offer.gas_consumption_kwh || "",
            gas_price_eur_per_kwh: offer.gas_price_eur_per_kwh || "0.12",
            heat_pump_pv_share: offer.heat_pump_pv_share || "0.30",
            fuel_cost_per_100km: offer.fuel_cost_per_100km || "",
            // E-Auto toggles
            has_ev: offer.has_ev || false,
            ev_pv_share: offer.ev_pv_share || "",
            self_consumption_quote: offer.self_consumption_quote || "",
            price_increase_pct: offer.price_increase_pct || "3",
            // Admin-only manual override
            manual_total_override: offer.manual_total_override || "",
            manual_total_reason: offer.manual_total_reason || "",
          });
          
          // Load existing installation images
          if (offer.installation_images && offer.installation_images.length > 0) {
            setInstallationImages(offer.installation_images);
          }
        } catch (error) {
          console.error("Failed to load offer:", error);
          toast.error("Fehler beim Laden des Angebots");
          navigate("/angebote");
          return;
        }
      } else {
        // Set defaults for new offer
        if (modulesRes.data.length > 0) {
          setFormData(prev => ({ ...prev, module_id: modulesRes.data[0].id }));
        }
        if (storagesRes.data.length > 0) {
          setFormData(prev => ({ ...prev, storage_id: storagesRes.data[0].id }));
        }
        if (invertersRes.data.length > 0) {
          setFormData(prev => ({ ...prev, inverter_id: invertersRes.data[0].id }));
        }
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = () => {
    // ============================================================
    // CATALOG_COST PRICING ENGINE - EINFACHE SUMME
    // Endpreis = Summe aller VK-Preise + zusätzliche Margen
    // KEINE Matrix, KEINE EK/0.5 Berechnung, KEINE Mindestmarge!
    // ============================================================

    // Benötige Tier-Auswahl für Preisberechnung
    if (!formData.tier) {
      setMissingCostError(null);
      setCalculatedPrice({
        pricingMode: pricingMode,
        errorReason: "Bitte wählen Sie zuerst eine Anlagenklasse (Standard oder Secure Plus)",
        costDataValid: false,
        base: 0,
        final: 0
      });
      return;
    }

    try {
      // ============================================================
      // KONSTANTEN
      // ============================================================
      const SALES_COMMISSION = 0.10;  // 10% Verkäuferprovision (nur für Anzeige)
      const MAX_UPSELL_ABSOLUTE = 2500;  // Max 2.500€ Überverkauf

      // Get selected components
      const module = modules.find(m => m.id === formData.module_id);
      const inverter = inverters.find(i => i.id === formData.inverter_id);
      const storage = storages.find(s => s.id === formData.storage_id);

      // ============================================================
      // CHECK FOR MISSING REQUIRED COMPONENTS
      // ============================================================
      const missingRequiredComponents = [];
      if (!module) missingRequiredComponents.push("Modul");
      if (!inverter) missingRequiredComponents.push("Wechselrichter");
      if (!storage) missingRequiredComponents.push("Speicher");

      if (missingRequiredComponents.length > 0) {
        const errorMsg = `Bitte wählen Sie: ${missingRequiredComponents.join(', ')}`;
        setMissingCostError(errorMsg);
        setCalculatedPrice({
          pricingMode: pricingMode,
          errorReason: errorMsg,
          costDataValid: false,
          missingCostComponents: [],
          base: 0,
          final: 0,
          debugInfo: { pricingMode: pricingMode, missingComponents: missingRequiredComponents }
        });
        return;
      }

      // ============================================================
      // INVERTER VALIDIERUNG (max_modules Check)
      // ============================================================
      let inverterWarning = null;
      if (inverter?.max_modules && formData.module_count > inverter.max_modules) {
        inverterWarning = `Wechselrichter '${inverter.name}' nur bis ${inverter.max_modules} Module geeignet. Bitte anderen wählen.`;
      }

      // ============================================================
      // 1. EK-PREISE (EINZIGE Rechenbasis für Preisbildung)
      // ============================================================
      const getEK = (component) => component?.purchase_price || component?.cost || 0;

      // Hauptkomponenten EK
      const moduleEK = getEK(module);
      const moduleTotalEK = moduleEK * formData.module_count;
      const inverterEK = getEK(inverter);
      const storageEK = getEK(storage);
      const storageExtensionEK = (storage?.extension_cost || 0) * formData.storage_extension_count;

      // Optionen EK (nur wenn aktiviert)
      const emsKiEK = (formData.tier === "secure_plus" && formData.ems_ki) ? getEK(emsKi) || 495 : 0;
      const backupEK = formData.backup ? 200 : 0;
      const meterCabinetEK = (formData.meter_cabinet && meterCabinet) ? getEK(meterCabinet) || 825 : 0;
      const groundingEK = (formData.grounding && grounding) ? getEK(grounding) || 375 : 0;
      
      let scaffoldingEK = 0;
      if (formData.scaffolding !== "nein" && scaffolding) {
        const unitEK = getEK(scaffolding) || 400;
        scaffoldingEK = formData.scaffolding === "1_traufe" ? unitEK : unitEK * 2;
      }
      
      const wallboxEK = (formData.wallbox !== "nein" && wallbox) ? getEK(wallbox) || 745 : 0;
      
      // Zwischenzähler EK (100€ pro Stück - fixer Preis)
      const intermediateMeterEK = formData.intermediate_meter_count * 100;

      // ============================================================
      // 2. CHECK FOR MISSING EK PRICES (BLOCKING)
      // ============================================================
      const missingCostComponents = [];
      if (moduleEK === 0) missingCostComponents.push(`Modul '${module.name}'`);
      if (inverterEK === 0) missingCostComponents.push(`Wechselrichter '${inverter.name}'`);
      if (storageEK === 0) missingCostComponents.push(`Speicher '${storage.name}'`);

      if (missingCostComponents.length > 0) {
        setMissingCostError(`EK fehlt bei Komponente: ${missingCostComponents.join(', ')}`);
      } else {
        setMissingCostError(null);
      }

      // ============================================================
      // 3. TOTAL_COST (EK-Gesamt) - für Margenberechnung
      // ============================================================
      const totalCost = moduleTotalEK + inverterEK + storageEK + storageExtensionEK +
                        emsKiEK + backupEK + meterCabinetEK + groundingEK + 
                        scaffoldingEK + wallboxEK + intermediateMeterEK;

      const costDataValid = totalCost > 0 && missingCostComponents.length === 0;

      // ============================================================
      // 4. VK-PREISE MIT ZUSÄTZLICHER MARGE
      // Formel: (VK + zusätzliche Marge) × Anzahl
      // ============================================================
      const getVK = (component) => component?.price || 0;
      const getMargin = (component) => component?.additional_margin || 0;

      // Module: (VK + Marge) × Anzahl
      const moduleVKWithMargin = (getVK(module) + getMargin(module)) * formData.module_count;
      
      // Wechselrichter: VK + Marge
      const inverterVKWithMargin = getVK(inverter) + getMargin(inverter);
      
      // Speicher: (VK + Marge) + Speichererweiterung-VK
      const storageVKWithMargin = getVK(storage) + getMargin(storage);
      const storageExtensionVK = (storage?.extension_sales_price || 0) * formData.storage_extension_count;

      // Optionen VK (ohne zusätzliche Marge, da Zusatzprodukte)
      const emsKiVK = (formData.tier === "secure_plus" && formData.ems_ki) ? getVK(emsKi) : 0;
      const backupVK = formData.backup ? (getVK({ price: 400 })) : 0;  // Backup fest 400€ VK
      const meterCabinetVK = (formData.meter_cabinet && meterCabinet) ? getVK(meterCabinet) : 0;
      const groundingVK = (formData.grounding && grounding) ? getVK(grounding) : 0;
      
      let scaffoldingVK = 0;
      if (formData.scaffolding !== "nein" && scaffolding) {
        const unitVK = getVK(scaffolding);
        scaffoldingVK = formData.scaffolding === "1_traufe" ? unitVK : unitVK * 2;
      }
      
      const wallboxVK = (formData.wallbox !== "nein" && wallbox) ? getVK(wallbox) : 0;
      
      // Zwischenzähler VK (100€ pro Stück - fixer Preis, gleich wie EK)
      const intermediateMeterVK = formData.intermediate_meter_count * 100;

      // ============================================================
      // DC-Kosten-VK: Summe aller DC-Kosten × Modulanzahl
      // AC-Kosten-VK: Bedingte Pauschalen basierend auf Angebotsauswahl
      // ============================================================
      const dcCostsVK = (installationCosts.dc_costs || []).reduce((sum, dc) => {
        const vkPerModule = parseFloat(dc.selling_price_per_module) || 0;
        return sum + (vkPerModule * formData.module_count);
      }, 0);
      
      // AC-Kosten: Optionale Positionen nur wenn im Angebot ausgewählt
      // Standard (immer): Wechselrichter & Speicher, AC-Hauptzuleitung, Kleinteile, Kabel
      // Optional: Zählerschrank (meter_cabinet), Backup, Wallbox
      const acCostsVK = (installationCosts.ac_costs || []).reduce((sum, ac) => {
        const name = (ac.name || '').toLowerCase();
        const vk = parseFloat(ac.selling_price) || 0;
        
        // Prüfe ob es eine optionale Position ist
        const isZaehlerschrank = name.includes('zählerschrank') || name.includes('zaehlerschrank');
        const isBackup = name.includes('backup');
        const isWallbox = name.includes('wallbox');
        
        // Optionale Positionen nur berechnen wenn ausgewählt
        if (isZaehlerschrank) {
          // Nur wenn Zählerschrank im Angebot ausgewählt ist
          return sum + (formData.meter_cabinet ? vk : 0);
        }
        if (isBackup) {
          // Nur wenn Backup im Angebot ausgewählt ist
          return sum + (formData.backup ? vk : 0);
        }
        if (isWallbox) {
          // Nur wenn Wallbox im Angebot ausgewählt ist (nicht "nein")
          return sum + (formData.wallbox !== "nein" ? vk : 0);
        }
        
        // Standard-Positionen (immer berechnen)
        return sum + vk;
      }, 0);

      // ============================================================
      // 5. VERKAUFSPREIS = Summe aller VK-Preise
      // Keine Matrix, kein minListPrice, kein EK/0.5!
      // ============================================================
      const totalVK = moduleVKWithMargin + inverterVKWithMargin + 
                      storageVKWithMargin + storageExtensionVK +
                      emsKiVK + backupVK + meterCabinetVK + groundingVK + 
                      scaffoldingVK + wallboxVK + intermediateMeterVK + dcCostsVK + acCostsVK;

      const basePrice = totalVK;

      // ============================================================
      // 6. ÜBERVERKAUF (Upsell) - max 10% oder 2.500€
      // ============================================================
      const requestedUpsellPercent = formData.oversell_percent || 0;
      const upsellFromPercent = basePrice * (requestedUpsellPercent / 100);
      const upsell = Math.min(upsellFromPercent, MAX_UPSELL_ABSOLUTE);
      
      const prelimPrice = basePrice + upsell;

      // ============================================================
      // 7. RABATTE (seasonal + admin) - ohne Margin Floor!
      // ============================================================
      let adminDiscountPercent = formData.discount_percent || 0;
      let seasonalDiscountPercent = formData.seasonal_discount_percent || 0;

      if (activeSeasonalDiscount) {
        seasonalDiscountPercent = Math.max(activeSeasonalDiscount.min_percent,
          Math.min(activeSeasonalDiscount.max_percent, seasonalDiscountPercent));
      } else {
        seasonalDiscountPercent = 0;
      }

      const actualSeasonalDiscount = prelimPrice * (seasonalDiscountPercent / 100);
      const actualAdminDiscount = prelimPrice * (adminDiscountPercent / 100);
      const totalDiscount = actualSeasonalDiscount + actualAdminDiscount;

      // ============================================================
      // 8. FINALE PREISBERECHNUNG - Einfach: VK - Rabatte
      // ============================================================
      const finalPriceAfterDiscounts = prelimPrice - totalDiscount;

      // ============================================================
      // 9. EMS-KI ABZUG (Secure Plus ohne EMS-KI)
      // ============================================================
      const emsKiProductVK = (emsKi?.price || 0) + (emsKi?.additional_margin || 0);
      const emsKiDiscount = (formData.tier === "secure_plus" && !formData.ems_ki) ? emsKiProductVK : 0;

      // ============================================================
      // 10. FINALE PREISE
      // ============================================================
      const subtotal = prelimPrice;
      const total = finalPriceAfterDiscounts;
      const display = total - emsKiDiscount;

      // Skonto for prepayment
      const skontoPercent = formData.payment_terms === "prepayment" ? 5 : 0;
      const skonto = display * (skontoPercent / 100);
      const finalPrice = display - skonto;

      // ============================================================
      // 11. MARGENBERECHNUNG (nur zur Anzeige)
      // ============================================================
      const provisionAmount = display * SALES_COMMISSION;
      const netMarginEUR = display - totalCost - provisionAmount;
      const effectiveMarginPercent = display > 0 ? (netMarginEUR / display) * 100 : 0;
      const grossMarginPercent = display > 0 ? ((display - totalCost) / display) * 100 : 0;

      // ============================================================
      // SET CALCULATED PRICE STATE
      // ============================================================
      setCalculatedPrice({
        pricingMode: pricingMode,
        // Hauptwerte
        base: basePrice,
        totalCost: totalCost,
        totalVK: totalVK,  // VK-Summe der Produkte
        subtotal: subtotal,
        total: total,
        display: display,
        final: finalPrice,
        // Rabatte & Upsell
        upsell: upsell,
        discount: actualAdminDiscount,
        seasonalDiscount: actualSeasonalDiscount,
        seasonalDiscountPercent: seasonalDiscountPercent,
        oversell: upsell,
        skonto: skonto,
        // Status
        costDataValid: costDataValid,
        missingCostComponents: missingCostComponents,
        inverterWarning: inverterWarning,
        // Margen
        grossMarginPercent: grossMarginPercent,
        effectiveMarginPercent: effectiveMarginPercent,
        marginEUR: display - totalCost,
        netMarginEUR: netMarginEUR,
        provisionAmount: provisionAmount,
        actualMargin: grossMarginPercent,
        commissionBase: display,
        // VK für Anzeige (mit Marge!)
        moduleTotalPrice: moduleVKWithMargin,
        inverterPrice: inverterVKWithMargin,
        storagePrice: storageVKWithMargin,
        storageExtension: storageExtensionVK,
        emsKiPrice: emsKiVK,
        emsKiProductPrice: (emsKi?.price || 0) + (emsKi?.additional_margin || 0),
        emsKiDiscount: emsKiDiscount,
        backup: backupVK,
        meterCabinet: meterCabinetVK,
        grounding: groundingVK,
        scaffolding: scaffoldingVK,
        wallbox: wallboxVK,
        intermediateMeter: intermediateMeterVK,
        dcCosts: dcCostsVK,
        acCosts: acCostsVK,
        insurance: 0,
        // Debug Info
        debugInfo: {
          pricingMode: pricingMode,
          totalCost: totalCost,
          totalVK: totalVK,
          dcCostsVK: dcCostsVK,
          acCostsVK: acCostsVK,
          basePrice: basePrice,
          upsell: upsell,
          finalPrice: finalPrice,
          grossMarginPercent: grossMarginPercent.toFixed(1),
          effectiveMarginPercent: effectiveMarginPercent.toFixed(1),
          moduleCount: formData.module_count,
          intermediateMeterCount: formData.intermediate_meter_count
        }
      });

    } catch (error) {
      console.error("Price calculation error:", error);
      setMissingCostError(`Preisberechnungsfehler: ${error.message}`);
      setCalculatedPrice({
        pricingMode: pricingMode,
        errorReason: `Preisberechnungsfehler: ${error.message}`,
        costDataValid: false,
        base: 0,
        final: 0,
        debugInfo: { pricingMode: pricingMode, error: error.message }
      });
    }
  };

  // Check if selected module allows insurance
  const selectedModule = modules.find(m => m.id === formData.module_id);
  const insuranceAvailable = selectedModule?.insurance_available || false;

  // Check if selected inverter supports backup
  const selectedInverter = inverters.find(i => i.id === formData.inverter_id);
  const backupCapableInverters = inverters.filter(i => i.backup_capable);
  const selectedInverterBackupCapable = selectedInverter?.backup_capable || false;

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (installationImages.length + files.length > 6) {
      toast.error("Maximal 6 Bilder erlaubt");
      return;
    }
    
    // Preview images locally first
    const newPreviews = [];
    for (const file of files) {
      const reader = new FileReader();
      const preview = await new Promise((resolve) => {
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
      newPreviews.push(preview);
    }
    setInstallationImages(prev => [...prev, ...newPreviews]);
    
    // If editing an existing offer, upload immediately
    if (isEditMode && offerId) {
      setUploadingImages(true);
      try {
        await offersAPI.uploadImages(offerId, files);
        toast.success(`${files.length} Bild(er) hochgeladen`);
      } catch (error) {
        toast.error(getErrorMessage(error, "Fehler beim Hochladen"));
        // Remove previews on error
        setInstallationImages(prev => prev.slice(0, -newPreviews.length));
      } finally {
        setUploadingImages(false);
      }
    }
  };
  
  const handleRemoveImage = (index) => {
    setInstallationImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_id || !formData.module_id || !formData.storage_id || !formData.inverter_id) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    // Validate inverter max modules (selectedInverter already defined above)
    if (selectedInverter?.max_modules && formData.module_count > selectedInverter.max_modules) {
      toast.error(`Der gewählte Wechselrichter unterstützt maximal ${selectedInverter.max_modules} Module`);
      return;
    }

    // ============================================================
    // SAFETY GUARD: Block saving if cost data is invalid
    // ============================================================
    if (pricingMode === "CATALOG_COST") {
      if (!calculatedPrice.costDataValid) {
        const missingComponents = calculatedPrice.missingCostComponents?.join(", ") || "Unbekannte Komponenten";
        toast.error(`EK-Preis fehlt: ${missingComponents}. Bitte EK-Preis im Katalog pflegen.`);
        return;
      }
      
      if (calculatedPrice.totalCost <= 0) {
        toast.error("Preisberechnung nicht möglich: Gesamtkosten sind 0. Bitte EK-Preise im Katalog prüfen.");
        return;
      }
    }

    // Reset insurance if module doesn't support it
    const submitData = {
      ...formData,
      // Ensure all numeric fields are properly typed as numbers
      module_count: parseInt(formData.module_count) || 10,
      storage_extension_count: parseInt(formData.storage_extension_count) || 0,
      intermediate_meter_count: parseInt(formData.intermediate_meter_count) || 0,
      discount_percent: parseFloat(formData.discount_percent) || 0,
      seasonal_discount_percent: parseFloat(formData.seasonal_discount_percent) || 0,
      extra_cost_percent: parseFloat(formData.extra_cost_percent) || 0,
      oversell_percent: parseFloat(formData.oversell_percent) || 0,
      // Wirtschaftlichkeitsberechnung Felder
      lifetime_years: formData.lifetime_years ? parseInt(formData.lifetime_years) : 25,
      price_increase_pct: formData.price_increase_pct ? parseFloat(formData.price_increase_pct) : 3,
      investment_cost: formData.investment_cost ? parseFloat(formData.investment_cost) : null,
      self_consumption_quote: formData.self_consumption_quote ? parseFloat(formData.self_consumption_quote) : null,
      heat_pump_consumption_kwh: formData.heat_pump_consumption_kwh ? parseFloat(formData.heat_pump_consumption_kwh) : null,
      vehicle_cost_per_100km_old: formData.vehicle_cost_per_100km_old ? parseFloat(formData.vehicle_cost_per_100km_old) : null,
      vehicle_cost_per_100km_ev: formData.vehicle_cost_per_100km_ev ? parseFloat(formData.vehicle_cost_per_100km_ev) : null,
      gas_consumption_kwh: formData.gas_consumption_kwh ? parseFloat(formData.gas_consumption_kwh) : null,
      gas_price_eur_per_kwh: formData.gas_price_eur_per_kwh ? parseFloat(formData.gas_price_eur_per_kwh) : null,
      heat_pump_pv_share: formData.heat_pump_pv_share ? parseFloat(formData.heat_pump_pv_share) : null,
      fuel_cost_per_100km: formData.fuel_cost_per_100km ? parseFloat(formData.fuel_cost_per_100km) : null,
      insurance: insuranceAvailable ? formData.insurance : false,
      // Seasonal discount metadata
      seasonal_discount_name: activeSeasonalDiscount?.name || null,
      seasonal_discount_period: activeSeasonalDiscount 
        ? `${new Date(activeSeasonalDiscount.start_date).toLocaleDateString('de-DE')} - ${new Date(activeSeasonalDiscount.end_date).toLocaleDateString('de-DE')}`
        : null,
      // Ensure independence forecast fields are properly typed
      annual_household_consumption_kwh: formData.include_independence_forecast && formData.annual_household_consumption_kwh 
        ? parseFloat(formData.annual_household_consumption_kwh) 
        : null,
      target_autarky_percent: formData.target_autarky_percent ? parseFloat(formData.target_autarky_percent) : 70,
      annual_pv_yield_kwh: formData.annual_pv_yield_kwh ? parseFloat(formData.annual_pv_yield_kwh) : null,
      // Wechselrechnung fields - ALWAYS convert if present, regardless of include_switch_calculation
      include_switch_calculation: formData.include_independence_forecast ? formData.include_switch_calculation : false,
      baseline_grid_price_gross_eur_kwh: formData.baseline_grid_price_gross_eur_kwh 
        ? parseFloat(formData.baseline_grid_price_gross_eur_kwh) 
        : null,
      avg_dynamic_rest_grid_price_gross_eur_kwh: formData.avg_dynamic_rest_grid_price_gross_eur_kwh 
        ? parseFloat(formData.avg_dynamic_rest_grid_price_gross_eur_kwh) 
        : null,
      feed_in_tariff_eur_kwh: formData.feed_in_tariff_eur_kwh 
        ? parseFloat(formData.feed_in_tariff_eur_kwh) 
        : null,
      // E-Auto / Elektromobilität - ALWAYS convert if present
      ev_annual_km: formData.ev_annual_km 
        ? parseInt(formData.ev_annual_km) 
        : 15000,
      ev_consumption_kwh_per_100km: formData.ev_consumption_kwh_per_100km 
        ? parseFloat(formData.ev_consumption_kwh_per_100km) 
        : 17.0,
      ev_pv_share: formData.ev_pv_share 
        ? parseFloat(formData.ev_pv_share) 
        : 0.5,
      // Include pricing metadata for backend logging
      pricing_metadata: {
        pricing_mode: pricingMode,
        total_cost: calculatedPrice.totalCost,
        total_vk: calculatedPrice.totalVK,
        final_price: calculatedPrice.display
      },
      // Admin-only: manual total override (for non-round discounts)
      manual_total_override: formData.manual_total_override && parseFloat(formData.manual_total_override) > 0
        ? parseFloat(formData.manual_total_override)
        : (formData.manual_total_override === "" || formData.manual_total_override === null ? 0 : undefined),
      manual_total_reason: formData.manual_total_reason || null
    };

    setSaving(true);

    try {
      if (isEditMode && offerId) {
        // Update existing offer
        await offersAPI.update(offerId, submitData);
        toast.success("Angebot erfolgreich aktualisiert");
        navigate(`/angebote/${offerId}`);
      } else {
        // Create new offer
        const response = await offersAPI.create(submitData);
        const newOfferId = response.data.id;
        
        // Upload images if any were selected
        if (installationImages.length > 0) {
          try {
            // Convert base64 images to files for upload
            const files = await Promise.all(installationImages.map(async (dataUrl, index) => {
              const res = await fetch(dataUrl);
              const blob = await res.blob();
              return new File([blob], `installation_${index + 1}.jpg`, { type: 'image/jpeg' });
            }));
            await offersAPI.uploadImages(newOfferId, files);
          } catch (imgError) {
            console.error("Image upload failed:", imgError);
            toast.warning("Angebot erstellt, aber Bilder konnten nicht hochgeladen werden");
          }
        }
        
        toast.success("Angebot erfolgreich erstellt");
        navigate(`/angebote/${newOfferId}`);
      }
    } catch (error) {
      console.error("Failed to save offer:", error);
      toast.error(getErrorMessage(error, isEditMode ? "Fehler beim Aktualisieren des Angebots" : "Fehler beim Erstellen des Angebots"));
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  // Get module watt for kWp calculation (use existing selectedModule)
  const moduleWatt = selectedModule?.specifications?.watt || 450;
  const kwp = (formData.module_count * (moduleWatt / 1000)).toFixed(2);

  // Check inverter compatibility (selectedInverter defined at top of component)
  const isInverterCompatible = !selectedInverter?.max_modules || formData.module_count <= selectedInverter.max_modules;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid={isEditMode ? "offer-edit-page" : "offer-create-page"}>
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold font-['Manrope']">
            {isEditMode ? "Angebot bearbeiten" : "Neues Angebot"}
          </h1>
          <p className="text-muted-foreground">
            {isEditMode && existingOffer 
              ? `${existingOffer.offer_number} - ${existingOffer.customer_name}` 
              : "Konfigurieren Sie die PV-Anlage"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Configuration */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Projekt</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Projekt auswählen *</Label>
                  <Select
                    value={formData.project_id}
                    onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                    disabled={isEditMode}
                  >
                    <SelectTrigger data-testid="project-select">
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map((project) => (
                        <SelectItem key={project.id} value={project.id}>
                          {project.project_number} - {project.customer_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Anlagenklasse Auswahl */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    Anlagenklasse *
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, tier: "standard" })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.tier === "standard" 
                          ? "border-orange-500 bg-orange-50 shadow-md" 
                          : "border-muted hover:border-orange-300"
                      }`}
                      data-testid="tier-standard"
                    >
                      <div className="font-semibold text-lg text-orange-600">Standard</div>
                      <p className="text-sm text-muted-foreground mt-1">Bewährte Qualität</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, tier: "secure_plus" })}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        formData.tier === "secure_plus" 
                          ? "border-blue-500 bg-blue-50 shadow-md" 
                          : "border-muted hover:border-blue-300"
                      }`}
                      data-testid="tier-secure-plus"
                    >
                      <div className="font-semibold text-lg text-blue-700">Secure Plus</div>
                      <p className="text-sm text-muted-foreground mt-1">Premium mit Extras</p>
                    </button>
                  </div>
                  {!formData.tier && (
                    <p className="text-sm text-amber-600 mt-2">
                      Bitte wählen Sie zuerst eine Anlagenklasse aus
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Einleitungstext</Label>
                  <Select
                    value={formData.intro_text_id}
                    onValueChange={(value) => setFormData({ ...formData, intro_text_id: value })}
                    disabled={!formData.tier}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.tier ? "Optional" : "Erst Anlagenklasse wählen"} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredTextBlocks.map((block) => (
                        <SelectItem key={block.id} value={block.id}>
                          {block.name}
                          {block.tier === "standard" && " (Standard)"}
                          {block.tier === "secure_plus" && " (Secure Plus)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Abschlusstext</Label>
                  <Select
                    value={formData.outro_text_id}
                    onValueChange={(value) => setFormData({ ...formData, outro_text_id: value })}
                    disabled={!formData.tier}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.tier ? "Optional" : "Erst Anlagenklasse wählen"} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredOutroTextBlocks.map((block) => (
                        <SelectItem key={block.id} value={block.id}>
                          {block.name}
                          {block.tier === "standard" && " (Standard)"}
                          {block.tier === "secure_plus" && " (Secure Plus)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* PV System - nur anzeigen wenn Tier gewählt */}
            {formData.tier && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sun className="h-5 w-5 text-accent" />
                  PV-Anlage
                  <Badge variant="outline" className={formData.tier === "secure_plus" ? "border-blue-500 text-blue-600" : "border-orange-500 text-orange-600"}>
                    {formData.tier === "secure_plus" ? "Secure Plus" : "Standard"}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Module */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Modultyp *</Label>
                    <Select
                      value={formData.module_id}
                      onValueChange={(value) => setFormData({ ...formData, module_id: value })}
                    >
                      <SelectTrigger data-testid="module-select">
                        <SelectValue placeholder="Bitte wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredModules.map((module) => (
                          <SelectItem key={module.id} value={module.id}>
                            {module.name} {module.specifications?.watt && `(${module.specifications.watt}W)`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label>Anzahl Module: {formData.module_count}</Label>
                      <span className="text-sm font-medium text-accent">{kwp} kWp</span>
                    </div>
                    <Slider
                      value={[formData.module_count]}
                      onValueChange={([value]) => setFormData({ ...formData, module_count: value })}
                      min={8}
                      max={60}
                      step={1}
                      className="mt-2"
                      data-testid="module-count-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>8 Module (3,6 kWp)</span>
                      <span>60 Module (27 kWp)</span>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Storage */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Battery className="h-4 w-4" />
                      Speichertyp *
                    </Label>
                    <Select
                      value={formData.storage_id}
                      onValueChange={(value) => setFormData({ ...formData, storage_id: value })}
                    >
                      <SelectTrigger data-testid="storage-select">
                        <SelectValue placeholder="Bitte wählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredStorages.map((storage) => (
                          <SelectItem key={storage.id} value={storage.id}>
                            {storage.name} ({storage.capacity_kwh || 10} kWh)
                            {storage.max_extensions > 0 && ` • erweiterbar`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Basis: {selectedStorage?.capacity_kwh || 10} kWh Speicher
                    </p>
                  </div>

                  {/* Speichererweiterung - nur anzeigen wenn der Speicher Erweiterungen unterstützt */}
                  {hasExtension && (
                    <div className="space-y-2 p-3 bg-muted/30 rounded-lg border">
                      <Label className="flex items-center gap-2">
                        <Battery className="h-4 w-4 text-blue-600" />
                        {selectedStorage?.extension_name || "Speichererweiterung"}
                      </Label>
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setFormData(prev => ({ 
                            ...prev, 
                            storage_extension_count: Math.max(0, prev.storage_extension_count - 1) 
                          }))}
                          disabled={formData.storage_extension_count <= 0}
                          data-testid="storage-extension-minus"
                        >
                          <span className="text-lg font-bold">−</span>
                        </Button>
                        <div className="flex-1 text-center">
                          <span className="text-2xl font-bold" data-testid="storage-extension-count">
                            {formData.storage_extension_count}
                          </span>
                          <span className="text-muted-foreground ml-2">Module</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => setFormData(prev => ({ 
                            ...prev, 
                            storage_extension_count: Math.min(maxExtensions, prev.storage_extension_count + 1) 
                          }))}
                          disabled={formData.storage_extension_count >= maxExtensions}
                          data-testid="storage-extension-plus"
                        >
                          <span className="text-lg font-bold">+</span>
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Max. {maxExtensions} Erweiterungsmodule
                        {formData.storage_extension_count > 0 && (
                          <span className="ml-2 text-blue-600 font-medium">
                            (+{(selectedStorage?.extension_capacity_kwh * formData.storage_extension_count).toFixed(2)} kWh | {formData.storage_extension_count} × {selectedStorage?.extension_sales_price?.toLocaleString('de-DE')} € = {(selectedStorage?.extension_sales_price * formData.storage_extension_count).toLocaleString('de-DE')} €)
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Inverter */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Wechselrichter *
                  </Label>
                  <Select
                    value={formData.inverter_id}
                    onValueChange={(value) => {
                      // When changing inverter, disable backup if new inverter doesn't support it
                      const newInverter = inverters.find(i => i.id === value);
                      if (formData.backup && !newInverter?.backup_capable) {
                        setFormData({ ...formData, inverter_id: value, backup: false });
                        toast.info("Backup wurde deaktiviert - der gewählte Wechselrichter unterstützt kein Backup");
                      } else {
                        setFormData({ ...formData, inverter_id: value });
                      }
                    }}
                  >
                    <SelectTrigger data-testid="inverter-select" className={!isInverterCompatible ? "border-destructive" : ""}>
                      <SelectValue placeholder="Bitte wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredInverters.map((inverter) => {
                        // Disable inverter if it can't handle the current module count
                        const insufficientCapacity = inverter.max_modules && formData.module_count > inverter.max_modules;
                        const backupIncompatible = formData.backup && !inverter.backup_capable;
                        const isDisabled = insufficientCapacity || backupIncompatible;
                        
                        return (
                          <SelectItem 
                            key={inverter.id} 
                            value={inverter.id}
                            disabled={isDisabled}
                          >
                            {inverter.name} {inverter.max_modules && `(max. ${inverter.max_modules} Module)`}
                            {inverter.backup_capable && " ⚡ Backup"}
                            {insufficientCapacity && " ❌"}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  {!isInverterCompatible && (
                    <p className="text-sm text-destructive">
                      Zu viele Module für diesen Wechselrichter (max. {selectedInverter?.max_modules})
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
            )}

            {/* Additional Options - nur anzeigen wenn Tier gewählt */}
            {formData.tier && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wrench className="h-5 w-5" />
                  Zusatzoptionen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Backup Option */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Zap className="h-4 w-4 text-blue-600" />
                      Backup / Notstrom
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {backupCapableInverters.length === 0 ? (
                        <span className="text-amber-600">Kein backupfähiger Wechselrichter verfügbar</span>
                      ) : formData.backup ? (
                        <span className="text-blue-600">+{formatCurrency(400)} Mehrpreis</span>
                      ) : (
                        "Notstromversorgung bei Stromausfall"
                      )}
                    </p>
                  </div>
                  <Switch
                    checked={formData.backup}
                    disabled={backupCapableInverters.length === 0}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        // Auto-select backup-capable inverter if current one doesn't support it
                        if (!selectedInverterBackupCapable && backupCapableInverters.length > 0) {
                          const backupInverter = backupCapableInverters[0];
                          setFormData({ ...formData, backup: true, inverter_id: backupInverter.id });
                          toast.success(`Wechselrichter automatisch auf "${backupInverter.name}" gewechselt (backupfähig)`);
                        } else {
                          setFormData({ ...formData, backup: true });
                        }
                      } else {
                        setFormData({ ...formData, backup: false });
                      }
                    }}
                    data-testid="backup-switch"
                  />
                </div>

                <Separator />

                {/* EMS-KI nur bei Secure Plus verfügbar */}
                {formData.tier === "secure_plus" && (
                <>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>EMS-KI System</Label>
                    <p className="text-sm text-muted-foreground">
                      {formData.ems_ki 
                        ? "Im Preis enthalten" 
                        : <span className="text-amber-600">-{formatCurrency(emsKi?.price || 990)} Abzug</span>
                      }
                    </p>
                  </div>
                  <Switch
                    checked={formData.ems_ki}
                    onCheckedChange={(checked) => setFormData({ ...formData, ems_ki: checked })}
                    data-testid="ems-ki-switch"
                  />
                </div>

                <Separator />
                </>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Neuer Zählerschrank</Label>
                    <p className="text-sm text-muted-foreground">{formatCurrency(meterCabinet?.price || 1500)}</p>
                  </div>
                  <Switch
                    checked={formData.meter_cabinet}
                    onCheckedChange={(checked) => setFormData({ ...formData, meter_cabinet: checked })}
                    data-testid="meter-cabinet-switch"
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <Label>Neue Erdung</Label>
                    <p className="text-sm text-muted-foreground">{formatCurrency(grounding?.price || 750)}</p>
                  </div>
                  <Switch
                    checked={formData.grounding}
                    onCheckedChange={(checked) => setFormData({ ...formData, grounding: checked })}
                    data-testid="grounding-switch"
                  />
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Gerüst</Label>
                  <Select
                    value={formData.scaffolding}
                    onValueChange={(value) => setFormData({ ...formData, scaffolding: value })}
                  >
                    <SelectTrigger data-testid="scaffolding-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nein">Nein</SelectItem>
                      <SelectItem value="1_traufe">1 Traufe ({formatCurrency(scaffolding?.price || 800)})</SelectItem>
                      <SelectItem value="2_traufe">2 Traufe ({formatCurrency((scaffolding?.price || 800) * 2)})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                <div className="space-y-2">
                  <Label>Wallbox</Label>
                  <Select
                    value={formData.wallbox}
                    onValueChange={(value) => setFormData({ ...formData, wallbox: value })}
                  >
                    <SelectTrigger data-testid="wallbox-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nein">Nein</SelectItem>
                      <SelectItem value="11kw">11 kW ({formatCurrency(wallbox?.price || 1490)})</SelectItem>
                      <SelectItem value="11kw_mid">11 kW MID ({formatCurrency(wallbox?.price || 1490)})</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator />

                {/* Zwischenzähler */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Zwischenzähler</Label>
                      <p className="text-sm text-muted-foreground">100,00 € pro Stück</p>
                    </div>
                    <Switch
                      checked={formData.intermediate_meter_count > 0}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        intermediate_meter_count: checked ? 1 : 0 
                      })}
                      data-testid="intermediate-meter-switch"
                    />
                  </div>
                  
                  {formData.intermediate_meter_count > 0 && (
                    <div className="flex items-center gap-3 pl-4 border-l-2 border-primary/20">
                      <Label className="text-sm whitespace-nowrap">Anzahl:</Label>
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        value={formData.intermediate_meter_count}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          intermediate_meter_count: Math.max(1, Math.min(20, parseInt(e.target.value) || 1))
                        })}
                        className="w-20 h-8"
                        data-testid="intermediate-meter-count"
                      />
                      <span className="text-sm text-muted-foreground">
                        = {formatCurrency(formData.intermediate_meter_count * 100)}
                      </span>
                    </div>
                  )}
                </div>

                {/* ERGO Insurance - only for modules with insurance_available */}
                {insuranceAvailable && formData.tier === "secure_plus" && (
                  <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                          <svg className="h-5 w-5 text-green-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                          </svg>
                        </div>
                        <div>
                          <Label className="text-green-800 dark:text-green-200 font-medium">
                            ERGO PV-Versicherung
                          </Label>
                          <p className="text-xs text-green-600 dark:text-green-400">
                            Kostenloser Versicherungsschutz für 1 Jahr
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={formData.insurance}
                        onCheckedChange={(checked) => setFormData({ ...formData, insurance: checked })}
                        data-testid="insurance-switch"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            )}

            {/* Admin-Switch für Admin-Modus */}
            {isAdmin && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <EyeOff className="h-4 w-4 text-amber-600" />
                      <Label className="text-amber-800 font-medium">Admin-Modus</Label>
                    </div>
                    <Switch
                      checked={hidePricingTable}
                      onCheckedChange={setHidePricingTable}
                      data-testid="hide-margin-switch"
                    />
                  </div>
                  <p className="text-xs text-amber-600 mt-1">
                    Blendet EK-Preise, Margenübersicht und Debug-Info aus
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Pricing - nur anzeigen wenn Tier gewählt */}
            {formData.tier && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Preisgestaltung
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Rabatt: {formData.discount_percent}%</Label>
                    <span className="text-sm text-green-600">-{formatCurrency(calculatedPrice.discount)}</span>
                  </div>
                  <Slider
                    value={[formData.discount_percent]}
                    onValueChange={([value]) => setFormData({ ...formData, discount_percent: value })}
                    min={0}
                    max={20}
                    step={0.5}
                    data-testid="discount-slider"
                  />
                  <p className="text-xs text-muted-foreground">Max. 20% - wird im Angebot angezeigt (beeinflusst Provision)</p>
                </div>

                {/* Saisonaler Rabatt - nur wenn aktiver Rabatt konfiguriert ist */}
                {activeSeasonalDiscount && (
                  <>
                    <Separator />
                    <div className="space-y-2 bg-amber-50 -mx-6 px-6 py-4 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Percent className="h-4 w-4 text-amber-600" />
                        <span className="font-semibold text-amber-800">{activeSeasonalDiscount.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <Label>Saisonaler Rabatt: {formData.seasonal_discount_percent}%</Label>
                        <span className="text-sm text-amber-600">-{formatCurrency(calculatedPrice.seasonalDiscount)}</span>
                      </div>
                      <Slider
                        value={[formData.seasonal_discount_percent]}
                        onValueChange={([value]) => setFormData({ ...formData, seasonal_discount_percent: value })}
                        min={activeSeasonalDiscount.min_percent}
                        max={activeSeasonalDiscount.max_percent}
                        step={0.5}
                        className="[&>span:first-child]:bg-amber-200 [&>span:first-child>span]:bg-amber-500"
                        data-testid="seasonal-discount-slider"
                      />
                      <div className="flex items-center gap-2 text-xs text-amber-700">
                        <Calendar className="h-3 w-3" />
                        <span>
                          Gültig: {new Date(activeSeasonalDiscount.start_date).toLocaleDateString('de-DE')} - {new Date(activeSeasonalDiscount.end_date).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                      <p className="text-xs text-amber-700">Beeinflusst NICHT die Provision des Vertrieblers</p>
                    </div>
                  </>
                )}

                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label>Zusätzliche Kosten: {formData.oversell_percent}%</Label>
                    <span className="text-sm text-muted-foreground">+{formatCurrency(calculatedPrice.oversell)}</span>
                  </div>
                  <Slider
                    value={[formData.oversell_percent]}
                    onValueChange={([value]) => setFormData({ ...formData, oversell_percent: value })}
                    min={0}
                    max={10}
                    step={0.5}
                    data-testid="oversell-slider"
                  />
                  <p className="text-xs text-muted-foreground">Max. 10% oder 2.500€ (je nachdem was niedriger ist) - wird NICHT im Angebot angezeigt</p>
                </div>

                <Separator />

                {/* Payment Terms */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Zahlungsbedingungen</Label>
                  <Select 
                    value={formData.payment_terms} 
                    onValueChange={(value) => setFormData({ ...formData, payment_terms: value })}
                  >
                    <SelectTrigger data-testid="payment-terms-select">
                      <SelectValue placeholder="Zahlungsbedingung wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">
                        <div className="flex flex-col">
                          <span className="font-medium">Standard (60/30/10)</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="prepayment">
                        <div className="flex flex-col">
                          <span className="font-medium">100% Vorkasse (5% Skonto)</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="text-xs text-muted-foreground space-y-1 p-3 bg-muted/50 rounded-lg">
                    {formData.payment_terms === "standard" ? (
                      <>
                        <p><strong>60%</strong> nach Auftragsbestätigung</p>
                        <p><strong>30%</strong> nach Fertigstellung Dacharbeiten</p>
                        <p><strong>10%</strong> nach Inbetriebnahme & Abnahme</p>
                      </>
                    ) : (
                      <>
                        <p><strong>100%</strong> Vorkasse nach Auftragsbestätigung</p>
                        <p className="text-green-600 font-medium">→ 5% Skonto vom Gesamtpreis</p>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            )}

            {/* Notizen für das Angebot */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Notizen
                </CardTitle>
                <CardDescription>
                  Optionale Notizen, die im Angebot-PDF erscheinen (z.B. besondere Vereinbarungen, technische Hinweise)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Notizen für das Angebot eingeben..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="resize-none"
                  data-testid="offer-notes-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Diese Notizen werden auf einer separaten Seite im PDF angezeigt (nur wenn ausgefüllt).
                </p>
              </CardContent>
            </Card>

            {/* Installation Images Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  Bilder der Modulfläche
                </CardTitle>
                <CardDescription>
                  Laden Sie bis zu 6 Bilder der geplanten Modulbelegung hoch (werden im Angebot angezeigt)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Image Preview Grid */}
                {installationImages.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {installationImages.map((img, index) => (
                      <div key={index} className="relative group aspect-video bg-gray-100 rounded-lg overflow-hidden">
                        <img 
                          src={img} 
                          alt={`Modulbild ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          type="button"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload Button */}
                {installationImages.length < 6 && (
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      multiple
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImages}
                      className="w-full border-dashed"
                    >
                      {uploadingImages ? (
                        <span className="animate-pulse">Hochladen...</span>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          Bilder hochladen ({installationImages.length}/6)
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      JPG, PNG oder HEIC. Bilder werden automatisch komprimiert.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Unabhängigkeitsrechnung (konservativ) */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Sun className="h-5 w-5" />
                  Unabhängigkeitsrechnung
                </CardTitle>
                <CardDescription>
                  Konservative Autarkie-Prognose im Angebot anzeigen
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="include_independence_forecast" className="font-medium">
                    Im Angebot anzeigen
                  </Label>
                  <Switch
                    id="include_independence_forecast"
                    checked={formData.include_independence_forecast}
                    onCheckedChange={(checked) => setFormData({ ...formData, include_independence_forecast: checked })}
                    data-testid="independence-forecast-switch"
                  />
                </div>
                
                {formData.include_independence_forecast && (
                  <div className="space-y-4 pt-4 border-t">
                    {/* Jahresverbrauch */}
                    <div>
                      <Label htmlFor="annual_household_consumption_kwh" className="text-sm font-medium">
                        Jahresverbrauch Haushalt (kWh) *
                      </Label>
                      <Input
                        id="annual_household_consumption_kwh"
                        type="number"
                        placeholder="z.B. 4500"
                        value={formData.annual_household_consumption_kwh}
                        onChange={(e) => setFormData({ ...formData, annual_household_consumption_kwh: e.target.value })}
                        className="mt-1"
                        min="0"
                        data-testid="annual-consumption-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Pflichtfeld - Angabe vom Kunden erforderlich
                      </p>
                    </div>

                    {/* Ziel-Autarkie */}
                    <div>
                      <Label htmlFor="target_autarky_percent" className="text-sm font-medium">
                        Ziel-Autarkie (%)
                      </Label>
                      <Input
                        id="target_autarky_percent"
                        type="number"
                        value={formData.target_autarky_percent}
                        onChange={(e) => setFormData({ ...formData, target_autarky_percent: e.target.value })}
                        onBlur={(e) => {
                          const val = parseFloat(e.target.value);
                          if (isNaN(val) || val < 10) {
                            setFormData({ ...formData, target_autarky_percent: 10 });
                          } else if (val > 95) {
                            setFormData({ ...formData, target_autarky_percent: 95 });
                          }
                        }}
                        className="mt-1"
                        min="10"
                        max="95"
                        data-testid="target-autarky-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Konservativer Standardwert: 70% (Min: 10%, Max: 95%)
                      </p>
                    </div>

                    {/* Dachausrichtung */}
                    <div>
                      <Label htmlFor="roof_orientation" className="text-sm font-medium">
                        Dachausrichtung
                      </Label>
                      <Select
                        value={formData.roof_orientation}
                        onValueChange={(value) => setFormData({ ...formData, roof_orientation: value })}
                      >
                        <SelectTrigger className="mt-1" data-testid="roof-orientation-select">
                          <SelectValue placeholder="Ausrichtung wählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="south">Süd (900 kWh/kWp)</SelectItem>
                          <SelectItem value="east_west">Ost-West (850 kWh/kWp)</SelectItem>
                          <SelectItem value="east">Ost (820 kWh/kWp)</SelectItem>
                          <SelectItem value="west">West (820 kWh/kWp)</SelectItem>
                          <SelectItem value="north">Nord (650 kWh/kWp)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Beeinflusst die konservative Ertragsschätzung
                      </p>
                    </div>

                    {/* Manueller PV-Ertrag (optional) */}
                    <div>
                      <Label htmlFor="annual_pv_yield_kwh" className="text-sm font-medium">
                        Jahresertrag PV (kWh) - optional
                      </Label>
                      <Input
                        id="annual_pv_yield_kwh"
                        type="number"
                        placeholder="Automatisch berechnet aus kWp × Ertragsfaktor"
                        value={formData.annual_pv_yield_kwh}
                        onChange={(e) => setFormData({ ...formData, annual_pv_yield_kwh: e.target.value })}
                        className="mt-1"
                        min="0"
                        data-testid="annual-pv-yield-input"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Leer lassen für automatische Berechnung (PV-Leistung: {kwp} kWp)
                      </p>
                    </div>

                    {/* Separator for Wechselrechnung Section */}
                    <div className="pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm flex items-center gap-2">
                          <Calculator className="h-4 w-4" />
                          Wechselrechnung: Stromkosten-Vorteil (konservativ)
                        </h4>
                        <div className="flex items-center gap-2">
                          <Label htmlFor="include_switch_calculation" className="text-xs text-muted-foreground">
                            Anzeigen
                          </Label>
                          <Switch
                            id="include_switch_calculation"
                            checked={formData.include_switch_calculation}
                            onCheckedChange={(checked) => setFormData({ ...formData, include_switch_calculation: checked })}
                            data-testid="include-switch-calculation-toggle"
                          />
                        </div>
                      </div>
                    </div>

                    {formData.include_switch_calculation && (
                      <>
                        {/* Bisheriger Strompreis (brutto) */}
                        <div>
                          <Label htmlFor="baseline_grid_price_gross_eur_kwh" className="text-sm font-medium">
                            Bisheriger Strompreis (brutto) (€/kWh) *
                          </Label>
                          <Input
                            id="baseline_grid_price_gross_eur_kwh"
                            type="number"
                            step="0.0001"
                            placeholder="z.B. 0.32"
                            value={formData.baseline_grid_price_gross_eur_kwh}
                            onChange={(e) => setFormData({ ...formData, baseline_grid_price_gross_eur_kwh: e.target.value })}
                            className="mt-1"
                            min="0"
                            data-testid="baseline-grid-price-input"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Aktueller Strompreis des Kunden (Endpreis inkl. aller Abgaben)
                          </p>
                        </div>

                        {/* Ø Reststrompreis (dynamischer Tarif, brutto) */}
                        <div>
                          <Label htmlFor="avg_dynamic_rest_grid_price_gross_eur_kwh" className="text-sm font-medium">
                            Ø Reststrompreis (dynamischer Tarif, brutto) (€/kWh) *
                          </Label>
                          <Input
                            id="avg_dynamic_rest_grid_price_gross_eur_kwh"
                            type="number"
                            step="0.0001"
                            placeholder="z.B. 0.26"
                            value={formData.avg_dynamic_rest_grid_price_gross_eur_kwh}
                            onChange={(e) => setFormData({ ...formData, avg_dynamic_rest_grid_price_gross_eur_kwh: e.target.value })}
                            className="mt-1"
                            min="0"
                            data-testid="avg-dynamic-rest-grid-price-input"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Erwarteter Durchschnittspreis bei dynamischem Tarif (brutto)
                          </p>
                        </div>

                        {/* Einspeisevergütung */}
                        <div>
                          <Label htmlFor="feed_in_tariff_eur_kwh" className="text-sm font-medium">
                            Einspeisevergütung (€/kWh) *
                          </Label>
                          <Input
                            id="feed_in_tariff_eur_kwh"
                            type="number"
                            step="0.0001"
                            placeholder="z.B. 0.08"
                            value={formData.feed_in_tariff_eur_kwh}
                            onChange={(e) => setFormData({ ...formData, feed_in_tariff_eur_kwh: e.target.value })}
                            className="mt-1"
                            min="0"
                            data-testid="feed-in-tariff-input"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Aktuelle EEG-Vergütung für Einspeisung
                          </p>
                        </div>

                        {/* E-Auto / Elektromobilität Separator */}
                        <div className="col-span-2 pt-2 pb-1 border-t border-dashed">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium text-purple-700 flex items-center gap-2">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              Elektromobilität (E-Auto)
                            </div>
                            <Switch
                              checked={formData.has_ev || false}
                              onCheckedChange={(checked) => setFormData({ ...formData, has_ev: checked })}
                              data-testid="has-ev-toggle"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formData.has_ev ? 'E-Auto in Wirtschaftlichkeitsberechnung aktiv' : 'E-Auto aktivieren für vollständige Berechnung'}
                          </p>
                        </div>

                        {/* E-Auto Felder nur anzeigen wenn aktiviert */}
                        {formData.has_ev && (
                          <>
                            {/* E-Auto Jährliche Fahrleistung */}
                            <div>
                              <Label htmlFor="ev_annual_km" className="text-sm font-medium">
                                Jährliche Fahrleistung (km)
                              </Label>
                              <Input
                                id="ev_annual_km"
                                type="number"
                                step="1000"
                                placeholder="z.B. 15000"
                                value={formData.ev_annual_km}
                                onChange={(e) => setFormData({ ...formData, ev_annual_km: e.target.value })}
                                className="mt-1"
                                min="0"
                                data-testid="ev-annual-km-input"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Durchschnitt in Deutschland: 15.000 km/Jahr
                              </p>
                            </div>

                            {/* E-Auto Verbrauch */}
                            <div>
                              <Label htmlFor="ev_consumption_kwh_per_100km" className="text-sm font-medium">
                                E-Auto Verbrauch (kWh/100km)
                              </Label>
                              <Input
                                id="ev_consumption_kwh_per_100km"
                                type="number"
                                step="0.5"
                                placeholder="z.B. 17"
                                value={formData.ev_consumption_kwh_per_100km}
                                onChange={(e) => setFormData({ ...formData, ev_consumption_kwh_per_100km: e.target.value })}
                                className="mt-1"
                                min="0"
                                data-testid="ev-consumption-input"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Durchschnitt E-Auto: 15-20 kWh/100km
                              </p>
                            </div>

                            {/* E-Auto PV-Anteil */}
                            <div>
                              <Label htmlFor="ev_pv_share" className="text-sm font-medium">
                                PV-Anteil beim Laden (%)
                              </Label>
                              <Input
                                id="ev_pv_share"
                                type="number"
                                step="5"
                                placeholder="z.B. 30"
                                value={formData.ev_pv_share ? (parseFloat(formData.ev_pv_share) * 100).toFixed(0) : '30'}
                                onChange={(e) => setFormData({ ...formData, ev_pv_share: (parseFloat(e.target.value) || 30) / 100 })}
                                className="mt-1"
                                min="0"
                                max="100"
                                data-testid="ev-pv-share-input"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Anteil der E-Auto-Ladung mit PV-Strom (Rest = Netzstrom)
                              </p>
                            </div>

                            {/* E-Auto Ersparnis Vorschau - KORRIGIERT: nur PV-Anteil als Ersparnis, Netzanteil als Kosten */}
                            {formData.ev_annual_km && formData.ev_consumption_kwh_per_100km && formData.baseline_grid_price_gross_eur_kwh && (
                              <div className="col-span-2 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                <div className="text-sm text-purple-800 space-y-2">
                                  {(() => {
                                    const evDemand = Math.round(parseInt(formData.ev_annual_km) * parseFloat(formData.ev_consumption_kwh_per_100km) / 100);
                                    const evSolarShare = parseFloat(formData.ev_pv_share) || 0.30;
                                    const evSolar = Math.round(evDemand * evSolarShare);
                                    const evGrid = evDemand - evSolar;
                                    const currentPrice = parseFloat(formData.baseline_grid_price_gross_eur_kwh);
                                    const dynamicPrice = parseFloat(formData.avg_dynamic_rest_grid_price_gross_eur_kwh) || currentPrice;
                                    const evSavings = evSolar * currentPrice;
                                    const evCost = evGrid * dynamicPrice;
                                    const evNet = evSavings - evCost;
                                    
                                    return (
                                      <>
                                        <div className="font-medium text-purple-700">E-Auto Ladebedarf: {evDemand.toLocaleString('de-DE')} kWh/Jahr</div>
                                        <div className="flex justify-between items-center">
                                          <span className="text-green-700">PV-Anteil ({(evSolarShare * 100).toFixed(0)}%):</span>
                                          <span className="font-medium text-green-600">
                                            {evSolar.toLocaleString('de-DE')} kWh × {currentPrice.toFixed(2)} €/kWh = <span className="font-bold">+{evSavings.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span> Ersparnis
                                          </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                          <span className="text-red-700">Netzladung ({((1 - evSolarShare) * 100).toFixed(0)}%):</span>
                                          <span className="font-medium text-red-600">
                                            {evGrid.toLocaleString('de-DE')} kWh × {dynamicPrice.toFixed(2)} €/kWh = <span className="font-bold">-{evCost.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span> Kosten
                                          </span>
                                        </div>
                                        <div className={`pt-2 border-t border-purple-300 flex justify-between items-center font-bold ${evNet >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                          <span>Nettoeffekt E-Auto:</span>
                                          <span>{evNet >= 0 ? '+' : ''}{evNet.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/Jahr</span>
                                        </div>
                                        <div className="pt-1 text-xs text-purple-600 italic">
                                          Diese Werte fließen in die Hauptwirtschaftlichkeitsberechnung ein
                                        </div>
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {/* Kraftstoffkosten alt (nur bei E-Auto) */}
                        {formData.has_ev && (
                          <div>
                            <Label htmlFor="fuel_cost_per_100km" className="text-sm font-medium">
                              Bisherige Kraftstoffkosten (€/100km)
                            </Label>
                            <Input
                              id="fuel_cost_per_100km"
                              type="number"
                              step="0.50"
                              placeholder="z.B. 10.00"
                              value={formData.fuel_cost_per_100km}
                              onChange={(e) => setFormData({ ...formData, fuel_cost_per_100km: e.target.value })}
                              className="mt-1"
                              min="0"
                              data-testid="fuel-cost-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              Benzin-/Dieselkosten die durch E-Auto wegfallen
                            </p>
                          </div>
                        )}

                        {/* Fahrkostenvergleich (nur bei E-Auto) */}
                        {formData.has_ev && (
                          <div className="col-span-2 p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-3">
                            <div className="text-sm font-medium text-violet-800">Fahrkostenvergleich</div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label htmlFor="vehicle_cost_per_100km_old" className="text-xs">
                                  Bisheriges Fahrzeug (€/100km)
                                </Label>
                                <Input
                                  id="vehicle_cost_per_100km_old"
                                  type="number"
                                  step="0.50"
                                  placeholder="z.B. 12.00"
                                  value={formData.vehicle_cost_per_100km_old}
                                  onChange={(e) => setFormData({ ...formData, vehicle_cost_per_100km_old: e.target.value })}
                                  className="mt-1"
                                  min="0"
                                  data-testid="vehicle-cost-old-input"
                                />
                              </div>
                              <div>
                                <Label htmlFor="vehicle_cost_per_100km_ev" className="text-xs">
                                  E-Auto (€/100km)
                                </Label>
                                <Input
                                  id="vehicle_cost_per_100km_ev"
                                  type="number"
                                  step="0.50"
                                  placeholder="z.B. 5.50"
                                  value={formData.vehicle_cost_per_100km_ev}
                                  onChange={(e) => setFormData({ ...formData, vehicle_cost_per_100km_ev: e.target.value })}
                                  className="mt-1"
                                  min="0"
                                  data-testid="vehicle-cost-ev-input"
                                />
                              </div>
                            </div>
                            <p className="text-xs text-violet-600 italic">
                              Optional: Wird separat von der Stromwirtschaftlichkeit ausgewiesen (keine Doppelzählung).
                            </p>
                          </div>
                        )}

                        {/* Wärmepumpe Separator */}
                        <div className="col-span-2 pt-2 pb-1 border-t border-dashed">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium text-orange-700 flex items-center gap-2">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                              </svg>
                              Wärmepumpe
                            </div>
                            <Switch
                              checked={formData.has_heat_pump || false}
                              onCheckedChange={(checked) => setFormData({ ...formData, has_heat_pump: checked })}
                              data-testid="has-heat-pump-toggle"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formData.has_heat_pump ? 'Wärmepumpe in Wirtschaftlichkeitsberechnung aktiv' : 'Wärmepumpe aktivieren für vollständige Berechnung'}
                          </p>
                        </div>

                        {formData.has_heat_pump && (
                          <>
                            <div>
                              <Label htmlFor="heat_pump_consumption_kwh" className="text-sm font-medium">
                                Jährlicher Stromverbrauch (kWh)
                              </Label>
                              <Input
                                id="heat_pump_consumption_kwh"
                                type="number"
                                step="100"
                                placeholder="z.B. 4000"
                                value={formData.heat_pump_consumption_kwh}
                                onChange={(e) => setFormData({ ...formData, heat_pump_consumption_kwh: e.target.value })}
                                className="mt-1"
                                min="0"
                                data-testid="heat-pump-consumption-input"
                              />
                              <p className="text-xs text-muted-foreground mt-1">
                                Typisch: 3.000-6.000 kWh/Jahr
                              </p>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label htmlFor="heat_pump_pv_share" className="text-sm font-medium">
                                  PV-Anteil Wärmepumpe
                                </Label>
                                <Input
                                  id="heat_pump_pv_share"
                                  type="number"
                                  step="0.05"
                                  value={formData.heat_pump_pv_share}
                                  onChange={(e) => setFormData({ ...formData, heat_pump_pv_share: e.target.value })}
                                  className="mt-1"
                                  min="0"
                                  max="1"
                                  data-testid="heat-pump-pv-share-input"
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Standard: 0.30 (30% konservativ)
                                </p>
                              </div>
                            </div>

                            <div className="col-span-2 pt-2 border-t border-dashed">
                              <div className="text-sm font-medium text-orange-700 mb-2">
                                Bisherige Heizkosten (Gas/Öl)
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label htmlFor="gas_consumption_kwh" className="text-xs">
                                    Gasverbrauch (kWh/Jahr)
                                  </Label>
                                  <Input
                                    id="gas_consumption_kwh"
                                    type="number"
                                    step="500"
                                    placeholder="z.B. 20000"
                                    value={formData.gas_consumption_kwh}
                                    onChange={(e) => setFormData({ ...formData, gas_consumption_kwh: e.target.value })}
                                    className="mt-1"
                                    min="0"
                                    data-testid="gas-consumption-input"
                                  />
                                </div>
                                <div>
                                  <Label htmlFor="gas_price_eur_per_kwh" className="text-xs">
                                    Gaspreis (€/kWh)
                                  </Label>
                                  <Input
                                    id="gas_price_eur_per_kwh"
                                    type="number"
                                    step="0.01"
                                    placeholder="z.B. 0.12"
                                    value={formData.gas_price_eur_per_kwh}
                                    onChange={(e) => setFormData({ ...formData, gas_price_eur_per_kwh: e.target.value })}
                                    className="mt-1"
                                    min="0"
                                    data-testid="gas-price-input"
                                  />
                                </div>
                              </div>
                              <p className="text-xs text-orange-600 italic mt-2">
                                Gaskosten fallen durch die Wärmepumpe vollständig weg und werden als Ersparnis gerechnet.
                              </p>
                            </div>

                            <div className="col-span-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-xs text-orange-800 space-y-1">
                              <div className="font-medium">Wärmepumpe: 40% PV / 60% Netz (feste Aufteilung)</div>
                              {formData.heat_pump_consumption_kwh && parseFloat(formData.heat_pump_consumption_kwh) > 0 && formData.baseline_grid_price_gross_eur_kwh && (() => {
                                const hpDemand = parseFloat(formData.heat_pump_consumption_kwh);
                                const hpPv = Math.round(hpDemand * 0.40);
                                const hpGrid = Math.round(hpDemand * 0.60);
                                const currentPrice = parseFloat(formData.baseline_grid_price_gross_eur_kwh);
                                const dynPrice = parseFloat(formData.avg_dynamic_rest_grid_price_gross_eur_kwh) || currentPrice;
                                const hpSavings = hpPv * currentPrice;
                                const hpCost = hpGrid * dynPrice;
                                const hpNet = hpSavings - hpCost;
                                return (
                                  <div className="space-y-1 mt-1">
                                    <div className="flex justify-between">
                                      <span className="text-green-700">PV-gedeckt (40%): {hpPv.toLocaleString('de-DE')} kWh</span>
                                      <span className="font-medium text-green-600">+{hpSavings.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} € Ersparnis</span>
                                    </div>
                                    <div className="flex justify-between">
                                      <span className="text-red-700">Netzbezug (60%): {hpGrid.toLocaleString('de-DE')} kWh</span>
                                      <span className="font-medium text-red-600">-{hpCost.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} € Kosten</span>
                                    </div>
                                    <div className={`pt-1 border-t border-orange-300 flex justify-between font-bold ${hpNet >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                      <span>Nettoeffekt Wärmepumpe:</span>
                                      <span>{hpNet >= 0 ? '+' : ''}{hpNet.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €/Jahr</span>
                                    </div>
                                    <div className="pt-1 text-xs text-orange-600 italic">
                                      Diese Werte fließen in die Hauptwirtschaftlichkeitsberechnung ein
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </>
                        )}

                        {/* Warning: PV yield too low */}
                        {formData.annual_pv_yield_kwh && formData.annual_household_consumption_kwh && 
                         parseFloat(formData.annual_pv_yield_kwh) < (parseFloat(formData.annual_household_consumption_kwh) * parseFloat(formData.target_autarky_percent || 70) / 100) && (
                          <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800">
                            <div className="font-medium">Hinweis</div>
                            <div>PV-Erzeugung ist kleiner als die angenommene autarke Energiemenge. Bitte Werte prüfen (Erzeugung/Autarkie).</div>
                          </div>
                        )}

                        {/* Warning: Dynamic price higher than baseline */}
                        {formData.avg_dynamic_rest_grid_price_gross_eur_kwh && formData.baseline_grid_price_gross_eur_kwh && 
                         parseFloat(formData.avg_dynamic_rest_grid_price_gross_eur_kwh) > parseFloat(formData.baseline_grid_price_gross_eur_kwh) && (
                          <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-800">
                            <div className="font-medium">Hinweis</div>
                            <div>Ø Reststrompreis liegt über dem bisherigen Strompreis. Bitte Werte prüfen.</div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Preview der Berechnung */}
                    {formData.annual_household_consumption_kwh && (
                      <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
                        <h4 className="font-medium text-sm mb-3">Vorschau der Prognose</h4>
                        <IndependenceForecastPreview 
                          consumption={parseFloat(formData.annual_household_consumption_kwh) || 0}
                          targetAutarky={parseFloat(formData.target_autarky_percent) || 70}
                          pvSizeKwp={kwp}
                          roofOrientation={formData.roof_orientation || 'south'}
                          manualYield={formData.annual_pv_yield_kwh ? parseFloat(formData.annual_pv_yield_kwh) : null}
                          storageKwh={(selectedStorage?.capacity_kwh || 10) + (formData.storage_extension_count * (selectedStorage?.extension_capacity_kwh || 0))}
                        />
                        
                        {/* Wechselrechnung Preview */}
                        {formData.include_switch_calculation && formData.baseline_grid_price_gross_eur_kwh && formData.avg_dynamic_rest_grid_price_gross_eur_kwh && formData.feed_in_tariff_eur_kwh && (
                          <div className="mt-4 pt-4 border-t">
                            <h5 className="font-medium text-sm mb-2">Wirtschaftlichkeitsberechnung (vermiedener Netzbezug)</h5>
                            <SwitchCalculationPreview 
                              consumption={parseFloat(formData.annual_household_consumption_kwh) || 0}
                              targetAutarky={parseFloat(formData.target_autarky_percent) || 70}
                              pvYield={formData.annual_pv_yield_kwh ? parseFloat(formData.annual_pv_yield_kwh) : (kwp * (formData.roof_orientation === 'south' ? 900 : formData.roof_orientation === 'east_west' ? 850 : formData.roof_orientation === 'east' || formData.roof_orientation === 'west' ? 820 : formData.roof_orientation === 'north' ? 650 : 850))}
                              baselinePrice={parseFloat(formData.baseline_grid_price_gross_eur_kwh) || 0}
                              dynamicPrice={parseFloat(formData.avg_dynamic_rest_grid_price_gross_eur_kwh) || 0}
                              feedInTariff={parseFloat(formData.feed_in_tariff_eur_kwh) || 0}
                              evAnnualKm={parseInt(formData.ev_annual_km) || 15000}
                              evConsumption={parseFloat(formData.ev_consumption_kwh_per_100km) || 17}
                              investmentCost={calculatedPrice.total || 20000}
                              selfConsumptionQuote={formData.self_consumption_quote}
                              evSolarShare={formData.ev_pv_share}
                              hasEV={formData.has_ev || false}
                              hasHeatPump={formData.has_heat_pump || false}
                              heatPumpConsumption={parseFloat(formData.heat_pump_consumption_kwh) || 0}
                              heatPumpPvShare={formData.heat_pump_pv_share}
                              gasConsumptionKwh={formData.gas_consumption_kwh}
                              gasPriceEurPerKwh={formData.gas_price_eur_per_kwh}
                              fuelCostPer100km={formData.fuel_cost_per_100km}
                              vehicleCostPer100kmOld={formData.vehicle_cost_per_100km_old}
                              vehicleCostPer100kmEv={formData.vehicle_cost_per_100km_ev}
                              batteryCapacity={(selectedStorage?.capacity_kwh || 10) + (formData.storage_extension_count * (selectedStorage?.extension_capacity_kwh || 0))}
                              priceIncreasePct={parseFloat(formData.price_increase_pct) || 3}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Price Summary */}
          <div className="space-y-6">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="font-['Manrope']">Preisübersicht</CardTitle>
                <CardDescription>
                  {formData.tier ? (
                    <>
                      {formData.module_count} Module | {kwp} kWp | {((selectedStorage?.capacity_kwh || 10) + (formData.storage_extension_count * (selectedStorage?.extension_capacity_kwh || 0))).toFixed(2)} kWh
                      <Badge variant="outline" className={`ml-2 ${formData.tier === "secure_plus" ? "border-blue-500 text-blue-600" : "border-orange-500 text-orange-600"}`}>
                        {formData.tier === "secure_plus" ? "Secure Plus" : "Standard"}
                      </Badge>
                    </>
                  ) : (
                    "Bitte zuerst Anlagenklasse wählen"
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* GENERAL ERROR BANNER - Show any pricing error */}
                {calculatedPrice.errorReason && (
                  <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-sm text-red-800">
                    <div className="font-medium mb-1">⚠️ Preisberechnung nicht möglich</div>
                    <div>{calculatedPrice.errorReason}</div>
                  </div>
                )}

                {/* SAFETY GUARD WARNINGS - Missing EK prices */}
                {pricingMode === "CATALOG_COST" && !calculatedPrice.costDataValid && calculatedPrice.missingCostComponents?.length > 0 && !calculatedPrice.errorReason && (
                  <div className="p-3 bg-red-100 border border-red-300 rounded-lg text-sm text-red-800">
                    <div className="font-medium mb-1">⚠️ EK-Preis fehlt</div>
                    <div>{calculatedPrice.missingCostComponents.join(", ")}</div>
                    <div className="text-xs mt-1">Bitte EK-Preis im Katalog pflegen. Speichern ist blockiert.</div>
                  </div>
                )}
                
                {/* PRICING MODE INDICATOR - hidden in admin mode */}
                {pricingMode === "CATALOG_COST" && !hidePricingTable && (
                  <div className="flex justify-between text-xs text-muted-foreground border-b pb-2">
                    <span>Preismodell</span>
                    <span className="font-mono">CATALOG_COST (VK-Summe)</span>
                  </div>
                )}

                {/* DEBUG INFO FOR ADMINS - hidden in admin mode */}
                {calculatedPrice.debugInfo && !hidePricingTable && (
                  <div className="text-xs text-muted-foreground border-b pb-2 space-y-1">
                    <div className="flex justify-between">
                      <span>Debug: Mode</span>
                      <span className="font-mono">{calculatedPrice.debugInfo.pricingMode}</span>
                    </div>
                    {calculatedPrice.debugInfo.totalCost !== undefined && (
                      <div className="flex justify-between">
                        <span>Debug: EK-Gesamt</span>
                        <span className="font-mono">{formatCurrency(calculatedPrice.debugInfo.totalCost)}</span>
                      </div>
                    )}
                    {calculatedPrice.debugInfo.totalVK !== undefined && (
                      <div className="flex justify-between">
                        <span>Debug: VK-Summe</span>
                        <span className="font-mono">{formatCurrency(calculatedPrice.debugInfo.totalVK)}</span>
                      </div>
                    )}
                    {calculatedPrice.debugInfo.moduleCount !== undefined && (
                      <div className="flex justify-between">
                        <span>Debug: Module</span>
                        <span className="font-mono">{calculatedPrice.debugInfo.moduleCount}</span>
                      </div>
                    )}
                    {calculatedPrice.debugInfo.error && (
                      <div className="flex justify-between text-red-600">
                        <span>Debug: Error</span>
                        <span className="font-mono">{calculatedPrice.debugInfo.error}</span>
                      </div>
                    )}
                  </div>
                )}

                {!formData.tier ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Wählen Sie eine Anlagenklasse (Standard oder Secure Plus), um die Preisberechnung zu starten.
                  </p>
                ) : calculatedPrice.errorReason ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Bitte beheben Sie den Fehler oben, um den Preis zu sehen.
                  </p>
                ) : (
                <div className="space-y-2 text-sm">
                  {/* ============================================ */}
                  {/* CATALOG_COST PRICING - Clear Labels         */}
                  {/* ============================================ */}
                  
                  {/* EK-Gesamt (Purchase Cost Total) - hidden in admin mode */}
                  {!hidePricingTable && (
                    <div className="flex justify-between font-medium bg-gray-50 dark:bg-gray-800 p-2 rounded">
                      <span>EK-Gesamt (Einkaufspreis)</span>
                      <span>{formatCurrency(calculatedPrice.totalCost || 0)}</span>
                    </div>
                  )}
                  
                  {!hidePricingTable && <Separator />}

                  {/* Component Breakdown (VK values) */}
                  <div className="text-xs text-muted-foreground">Komponenten (Verkaufspreis)</div>
                  
                  {/* Module */}
                  <div className="flex justify-between text-muted-foreground">
                    <span>Module ({formData.module_count}×)</span>
                    <span>{formatCurrency(calculatedPrice.moduleTotalPrice || 0)}</span>
                  </div>
                  
                  {/* Inverter */}
                  <div className="flex justify-between text-muted-foreground">
                    <span>Wechselrichter</span>
                    <span>{formatCurrency(calculatedPrice.inverterPrice || 0)}</span>
                  </div>
                  
                  {/* Storage */}
                  <div className="flex justify-between text-muted-foreground">
                    <span>Speicher</span>
                    <span>{formatCurrency(calculatedPrice.storagePrice || 0)}</span>
                  </div>

                  {/* Storage Extension */}
                  {calculatedPrice.storageExtension > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>+ Speichererweiterung ({formData.storage_extension_count}×)</span>
                      <span>{formatCurrency(calculatedPrice.storageExtension)}</span>
                    </div>
                  )}
                  
                  {/* EMS-KI Discount (Secure Plus only) */}
                  {formData.tier === "secure_plus" && calculatedPrice.emsKiDiscount > 0 && (
                    <div className="flex justify-between text-amber-600">
                      <span>- Ohne EMS-KI System</span>
                      <span>-{formatCurrency(calculatedPrice.emsKiDiscount)}</span>
                    </div>
                  )}
                  
                  {/* Backup */}
                  {calculatedPrice.backup > 0 && (
                    <div className="flex justify-between text-blue-600">
                      <span>+ Backup / Notstrom</span>
                      <span>+{formatCurrency(calculatedPrice.backup)}</span>
                    </div>
                  )}
                  
                  {/* Additional Components */}
                  {calculatedPrice.meterCabinet > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>+ Zählerschrank</span>
                      <span>{formatCurrency(calculatedPrice.meterCabinet)}</span>
                    </div>
                  )}
                  {calculatedPrice.grounding > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>+ Erdung</span>
                      <span>{formatCurrency(calculatedPrice.grounding)}</span>
                    </div>
                  )}
                  {calculatedPrice.scaffolding > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>+ Gerüst</span>
                      <span>{formatCurrency(calculatedPrice.scaffolding)}</span>
                    </div>
                  )}
                  {calculatedPrice.wallbox > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>+ Wallbox</span>
                      <span>{formatCurrency(calculatedPrice.wallbox)}</span>
                    </div>
                  )}
                  
                  {/* Installation costs */}
                  {calculatedPrice.dcCosts > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>+ DC-Montage ({formData.module_count}×)</span>
                      <span>{formatCurrency(calculatedPrice.dcCosts)}</span>
                    </div>
                  )}
                  {calculatedPrice.acCosts > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>+ AC-Montage</span>
                      <span>{formatCurrency(calculatedPrice.acCosts)}</span>
                    </div>
                  )}
                  
                  <Separator />
                  
                  {/* Subtotal */}
                  <div className="flex justify-between">
                    <span>Zwischensumme</span>
                    <span>{formatCurrency(calculatedPrice.subtotal)}</span>
                  </div>
                  
                  {/* Discounts */}
                  {calculatedPrice.discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>- Rabatt ({formData.discount_percent}%)</span>
                      <span>-{formatCurrency(calculatedPrice.discount)}</span>
                    </div>
                  )}
                  
                  {/* Saisonaler Rabatt */}
                  {calculatedPrice.seasonalDiscount > 0 && activeSeasonalDiscount && (
                    <div className="flex justify-between text-amber-600">
                      <div className="flex flex-col">
                        <span>- {activeSeasonalDiscount.name} ({calculatedPrice.seasonalDiscountPercent}%)</span>
                        <span className="text-xs">
                          Gültig: {new Date(activeSeasonalDiscount.start_date).toLocaleDateString('de-DE')} - {new Date(activeSeasonalDiscount.end_date).toLocaleDateString('de-DE')}
                        </span>
                      </div>
                      <span>-{formatCurrency(calculatedPrice.seasonalDiscount)}</span>
                    </div>
                  )}
                </div>
                )}

                {formData.tier && (
                <>
                <Separator />

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Kundenpreis</span>
                    <span className={`text-2xl font-bold font-['Manrope'] ${formData.payment_terms === "prepayment" ? "text-muted-foreground line-through" : (formData.manual_total_override && parseFloat(formData.manual_total_override) > 0 ? "text-muted-foreground line-through" : "text-accent")}`}>
                      {formatCurrency(calculatedPrice.display)}
                    </span>
                  </div>

                  {/* Admin-only: Manual Total Override */}
                  {isAdmin && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg space-y-2" data-testid="manual-total-override-section">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="manual_total_override" className="text-xs font-semibold text-amber-700 dark:text-amber-300">
                          Manueller Endbetrag (Admin)
                        </Label>
                        {formData.manual_total_override && parseFloat(formData.manual_total_override) > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-red-600 hover:text-red-700"
                            onClick={() => setFormData(prev => ({ ...prev, manual_total_override: "", manual_total_reason: "" }))}
                            data-testid="reset-manual-total-btn"
                          >
                            Zurücksetzen
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Input
                            id="manual_total_override"
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="z.B. 25000.00"
                            value={formData.manual_total_override}
                            onChange={(e) => setFormData(prev => ({ ...prev, manual_total_override: e.target.value }))}
                            data-testid="manual-total-override-input"
                          />
                          <p className="text-[10px] text-muted-foreground mt-1">Brutto-Endbetrag (€)</p>
                        </div>
                        <div>
                          <Input
                            id="manual_total_reason"
                            type="text"
                            placeholder="Grund / Notiz"
                            value={formData.manual_total_reason}
                            onChange={(e) => setFormData(prev => ({ ...prev, manual_total_reason: e.target.value }))}
                            data-testid="manual-total-reason-input"
                          />
                          <p className="text-[10px] text-muted-foreground mt-1">Optional: Begründung</p>
                        </div>
                      </div>
                      {formData.manual_total_override && parseFloat(formData.manual_total_override) > 0 && (
                        <div className="flex justify-between items-center pt-2 border-t border-amber-200 dark:border-amber-800">
                          <span className="font-semibold text-amber-700 dark:text-amber-300">Überschriebener Endbetrag</span>
                          <span className="text-2xl font-bold font-['Manrope'] text-amber-700 dark:text-amber-300" data-testid="manual-total-override-display">
                            {formatCurrency(parseFloat(formData.manual_total_override))}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Skonto for prepayment */}
                  {formData.payment_terms === "prepayment" && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg space-y-2">
                      <div className="flex justify-between text-green-600">
                        <span>- 5% Skonto (Vorkasse)</span>
                        <span>-{formatCurrency(calculatedPrice.skonto)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="font-semibold">Endpreis</span>
                        <span className="text-2xl font-bold font-['Manrope'] text-green-600">
                          {formatCurrency(calculatedPrice.final)}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {calculatedPrice.oversell > 0 && (
                    <div className="p-3 bg-muted rounded-lg text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Interner Preis</span>
                        <span className="font-semibold">{formatCurrency(calculatedPrice.total)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        inkl. {formData.oversell_percent}% Zusätzliche Kosten
                      </p>
                    </div>
                  )}

                  {/* MARGIN INFO - Only in CATALOG_COST mode, hidden if admin switch is on */}
                  {pricingMode === "CATALOG_COST" && calculatedPrice.totalCost > 0 && !hidePricingTable && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm space-y-1">
                      <div className="text-xs font-medium text-muted-foreground mb-2">Preiskalkulation</div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>EK-Gesamt</span>
                        <span className="font-mono">{formatCurrency(calculatedPrice.totalCost)}</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>VK-Summe (Listenpreis)</span>
                        <span className="font-mono">{formatCurrency(calculatedPrice.componentVKTotal || calculatedPrice.base)}</span>
                      </div>
                      <Separator className="my-1" />
                      <div className="text-xs font-medium text-muted-foreground mb-1">Margenübersicht</div>
                      <div className="flex justify-between">
                        <span>Brutto-Marge (€)</span>
                        <span className="font-mono">{formatCurrency(calculatedPrice.marginEUR || (calculatedPrice.display - calculatedPrice.totalCost))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Brutto-Marge (%)</span>
                        <span className={`font-mono ${calculatedPrice.grossMarginPercent >= 50 ? 'text-green-600' : calculatedPrice.grossMarginPercent >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {calculatedPrice.grossMarginPercent?.toFixed(1) || calculatedPrice.actualMargin?.toFixed(1)}%
                        </span>
                      </div>
                      <Separator className="my-1" />
                      <div className="flex justify-between text-muted-foreground">
                        <span>- Vertriebsprovision (10%)</span>
                        <span className="font-mono">-{formatCurrency(calculatedPrice.provisionAmount || (calculatedPrice.display * 0.10))}</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>= Netto-Firmenmarge (€)</span>
                        <span className="font-mono">{formatCurrency(calculatedPrice.netMarginEUR || ((calculatedPrice.display - calculatedPrice.totalCost) - (calculatedPrice.display * 0.10)))}</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>= Effektive Firmenmarge (%)</span>
                        <span className={`font-mono ${(calculatedPrice.effectiveMarginPercent || 40) >= 40 ? 'text-green-600' : calculatedPrice.effectiveMarginPercent >= 30 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {calculatedPrice.effectiveMarginPercent?.toFixed(1) || ((((calculatedPrice.display - calculatedPrice.totalCost) - (calculatedPrice.display * 0.10)) / calculatedPrice.display) * 100).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12" 
                  disabled={saving || !isInverterCompatible || !formData.tier || (pricingMode === "CATALOG_COST" && !calculatedPrice.costDataValid)}
                  data-testid={isEditMode ? "update-offer-btn" : "create-offer-btn"}
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent" />
                      {isEditMode ? "Aktualisiere Angebot..." : "Erstelle Angebot..."}
                    </span>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {isEditMode ? "Angebot aktualisieren" : "Angebot erstellen"}
                    </>
                  )}
                </Button>
                </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
